/**
 * Ensivasteen toimintaohjeet (Satakunnan sairaanhoitopiiri / Minna Huuhka 2016)
 * Kertaus- ja toimintamallit hälytyskoodeittain:
 * - Mitä kysytään (esitiedot, haastattelu)
 * - Mitä mitataan (vitaalit, raja-arvot)
 * - Mitä tehdään (ABCDE, toimenpiteet, asentohoito, lääkkeet)
 */

export const ENSIVASTE_OHJEET = [
  {
    code: "700",
    codes: ["700", "701"],
    title: "Eloton potilas / Elvytys (Aikuinen & Murrosikäinen)",
    category: "resuscitation",
    categoryLabel: "Elottomuus & Elvytys",
    urgency: "A",
    shortSummary:
      "Totea elottomuus alle 10 sekunnissa. Aloita heti painelu-puhalluselvytys (PPE 30:2) kovalla alustalla. Kiinnitä sydäniskuri (AED) painelun aikana. Kolmen käden maskiventilaatio lisähapella (O2).",
    happisuositus: {
      maski: "Elvytyspalje (Ambu) varaajapussilla",
      virtaus: "12–15 l/min",
      taso: "100 % O2 (Elvytys)",
      ohje: "Kaksin käsin tiivis C-E -maskipito 30:2. Oman kierron palattua (ROSC) vaihda varaajapussimaskiin (10–15 l/min).",
    },
    pikaPikaPika: [
      "VÄLITÖN PPE 30:2 kovalla alustalla (taajuus 100/min, syvyys 5–6 cm) – rinnan annettava palautua täysin, minimoi kaikki tauot!",
      "AED HETI KIINNI rintaan painelun aikana, laite päälle ja noudata kehotteita – keskeytä painelu vain analyysin ja iskun ajaksi!",
      "3-KÄDEN MASKIVENTILAATIO 100 % O2 (Ambu-elvytyspalje 12–15 l/min) + aseta nieluputki heti.",
      "VAIHDA PAINAJAA 2 min välein. DNR-elvytyksestä pidättäytyminen VAIN ESA 00 / L4 määräyksestä!",
    ],
    safety: [
      "Varmista oma ja tiimin työturvallisuus (sähkö, myrkyt, liikenne, väkivalta).",
      "Siirrä potilas tarvittaessa hätäsiirrolla kovalle alustalle ja tee riittävästi tilaa ympärille.",
    ],
    mitaKysytaan: [
      "Hälytyskeskuksen puhelun alkamisaika?",
      "Nähtiinkö tai kuultiinko potilaan menevän elottomaksi? (Todistettu tuupertuminen / witnessed arrest)",
      "Soitettiinko apua heti elottomuuden havaitsemisen jälkeen?",
      "Potilaan tavoittamisaika?",
      "Oliko annettu maallikkoelvytystä ja millaista sen laatu oli?",
      "Milloin elottomana löydetty potilas on viimeksi varmuudella nähty hereillä?",
      "Tiedossa olevat perussairaudet tai oireet ennen tuupertumista (esim. rintakipu, hengenahdistus)?",
      "Onko potilaalla elvytyskielto (DNR / hoitotahto / ei elvytetä)? (HUOM: elvytyksestä pidättäytyminen VAIN ESA 00 / L4 määräyksestä!).",
    ],
    mitaMitataan: [
      "Elottomuuden varmistus (< 10 s): ei reagoi käsittelyyn/puhutteluun, ei hengitä normaalisti (tai vain haukkovia kuolonhenkäyksiä eli agonaalista hengitystä). HUOM: Älä tuhlaa aikaa sykkeen tunnusteluun!",
      "Sydämen rytmi sydäniskurilla (AED): iskettävä rytmi (kammiovärinä VF tai sykkeetön kammiotakykardia VT) -> anna isku; ei-iskettävä rytmi (asystole eli tasaviiva tai sykkeetön sähköinen toiminta PEA) -> jatka heti painelu-puhalluselvytystä (PPE).",
      "Jos oma verenkierto palautuu (ROSC / Return of Spontaneous Circulation):",
      "- Kirjaa kaulasykkeen tuntumisen tarkka kellonaika (verenkierron palautumisaika / ROSC-aika).",
      "- Verenpaine (RR) 2–3 minuutin välein (jos yläpaine eli systolinen verenpaine < 120 mmHg -> nosta jalat koholle).",
      "- Happisaturaatio eli veren happipitoisuus (SpO2, tavoite 94–98 %).",
      "- Hengitystaajuus (HT / hengityskertaa minuutissa) ja sydämen syke tiheästi seurattuna.",
    ],
    mitaTehdaan: [
      "1. Hätäsiirto kovalle alustalle ja rintakehän paljastaminen välittömästi.",
      "2. Aloita painelu-puhalluselvytys (PPE) rytmillä 30:2 viipymättä:",
      "   - Painelutaajuus 100 krt/min, painelusyvyys aikuisella 5–6 cm.",
      "   - Rintakehän annettava palautua täysin painallusten välillä.",
      "   - Keskeytykset minimoidaan! Painelua jatketaan myös sydäniskurin (AED) latautuessa.",
      "3. Kiinnitä sydäniskurin (AED) elektrodit painelun aikana ja kytke laite päälle. Painelu keskeytetään VAIN rytmianalyysin ja iskun ajaksi.",
      "4. Aseta nieluputki potilaalle painelun aikana ilmatien varmistamiseksi.",
      "5. Ota käyttöön 'kolmen käden maskiventilaatio':",
      "   - Toinen ensiauttaja pitää maskia kaksin käsin tiukasti kasvoilla ja tukee leuan asentoa.",
      "   - Toinen ensiauttaja puristaa hengityspaljetta (liitettynä 100 % lisähappeen ja varaajapussiin, virtaus 12–15 l/min).",
      "6. Vaihda painajaa 2 minuutin välein elvytyksen laadun takaamiseksi.",
      "7. Varmista lisäapu: lääkäriyksikkö (ESA 00 / lääkärihelikopteri), ensihoidon kenttäjohtaja (L4) tai hoitotason ambulanssi.",
      "8. Verenkierron palautuessa (ROSC):",
      "   - Avusta hengitystä elvytyspalkeella vaikka potilaalla olisi omaa hengitystä.",
      "   - Jätä elektrodit rintaan ja kuuntele AED-sydäniskuria – varaudu uuteen sydänpysähdykseen!",
    ],
    erityishuomiot: [
      "Elvytyksestä pidättäydytään VAIN jos:",
      "- Potilaalla on voimassa oleva hoitotestamentti tai elvytyskielto (DNR) — VAIN ensihoitolääkärin (ESA 00) tai kenttäjohtajan (L4) nimenomaisesta määräyksestä!",
      "- Havaittavissa on kiistattomat toissijaiset kuolemanmerkit: kuolonkankeus (rigor mortis), lautumat (sinipunaiset kuolontäplät ihossa), mätäneminen tai elvytyksen estävä vamma (murskautuminen/irtileikkautuminen).",
      "- Epäselvissä tapauksissa aloitetaan aina elvytys ja konsultoidaan lääkäriyksikköä (ESA 00) tai kenttäjohtajaa (L4).",
    ],
  },
  {
    code: "700_lapsi",
    codes: ["700 (lapsi)", "701 (lapsi)"],
    title: "Eloton lapsi / Alle murrosikäisen elvytys",
    category: "resuscitation",
    categoryLabel: "Elottomuus & Elvytys",
    urgency: "A",
    shortSummary:
      "Lapsen elottomuudessa syynä lähes aina hapenpuute (hypoksia). Aloita heti 5 alkupuhalluksella! Elvytysrytmi 15:2. Huolehdi lämpötaloudesta.",
    happisuositus: {
      maski: "Lasten elvytyspalje varaajapussilla",
      virtaus: "12–15 l/min",
      taso: "100 % O2 (Lapsielvytys)",
      ohje: "5 alkupuhallusta. Huomioi lapsen koon mukainen maski ja varovainen puhallustilavuus (rintakehä nousee kevyesti).",
    },
    pikaPikaPika: [
      "SYYNÄ LÄHES AINA HYPOKSIA: Varmista ilmatiet ja ANNA HETI 5 ALKUPUHALLUSTA (varmista että rintakehä nousee)!",
      "PPE RYTMILLÄ 15:2: Painelusyvyys 1/3 rintakehästä (vauva ~4 cm 2 sormella, lapsi ~5 cm kämmentyvellä, 100–120/min).",
      "AED HETI KIINNI lastenelektrodeilla tai lapsikytkimellä.",
      "ESTÄ JÄÄHTYMINEN (hypotermia): peittele lapsi lämpimästi. Ventilaatio palkeella 100 % O2 (12–15 l/min).",
    ],
    safety: [
      "Työturvallisuus ja tilanteen nopea rauhoittaminen läsnäolijoille.",
      "Hätäsiirto kovalle tasaiselle alustalle.",
    ],
    mitaKysytaan: [
      "Mitä lapsi teki ennen elottomaksi menemistä? (Vierasesineen mahdollisuus suussa/hengitysteissä?)",
      "Milloin nähty viimeksi hereillä?",
      "Onko ollut sairastelua, kuumetta, hengitysvaikeutta, allergiaa tai kouristelua?",
      "Kuinka nopeasti tila kehittyi?",
    ],
    mitaMitataan: [
      "Elottomuuden toteaminen (< 10 s): ei reagoi käsittelyyn, ei hengitä tai hengitys epänormaalia/haukkovia.",
      "Ventilaation perillemeno: nouseeko rintakehä puhalluksilla?",
      "Rytmin analyysi sydäniskurilla (AED lastenelektrodeilla tai lapsikytkimellä).",
      "Verenkierron palautuessa (ROSC): syke napanuorasta/kaulalta/reisivaltimosta, happisaturaatio eli veren happipitoisuus (SpO2), hengitystaajuus (HT), lämpötila.",
    ],
    mitaTehdaan: [
      "1. Varmista ilmatiet ja ANNA HETI 5 ALKUPUHALLUSTA.",
      "   - Tarkista, että rintakehä nousee. Jos ei nouse, korjaa pään asento (lapsella neutraaliasento, rintalastan/hartioiden alle pieni pyyhe) ja poista näkyvä vierasesine.",
      "2. Aloita painelu-puhalluselvytys (PPE) rytmillä 15:2:",
      "   - Painelutaajuus 100–120 krt/min.",
      "   - Syvyys 1/3 rintakehän syvyydestä (n. 4 cm vauvalla, 5 cm lapsella).",
      "   - Pienellä lapsella kahdella sormella tai yhden käden kämmentyvellä, isommalla kahdella kädellä.",
      "3. Kiinnitä sydäniskuri (AED, käytä lastenelektrodeja tai lapsikytkintä jos saatavilla).",
      "4. Kolmen käden maskiventilaatio elvytyspalkeella (Ambu) ja 100 % lisähapella (12–15 l/min).",
      "5. Lapsen lämpötalouden suojaaminen – peittele lapsi ja estä jäähtyminen (hypotermia).",
      "6. Verenkierron palautuessa (ROSC) avusta ventilaatiota 100 % O2 n. 25 krt/min ja seuraa sykettä tiheästi.",
    ],
    erityishuomiot: [
      "Jos olet yksin lapsen luona, elvytä 1 minuutti ennen kuin poistut hälyttämään lisäapua.",
    ],
  },
  {
    code: "702",
    codes: ["702"],
    title: "Tajuttomuus",
    category: "consciousness",
    categoryLabel: "Tajunta",
    urgency: "A / B",
    shortSummary:
      "Turvaa ilmatiet ja aseta kylkiasentoon! Mittaa välittömästi verensokeri (B-Gluk). Muista VOI IHME -muistisääntö.",
    happisuositus: {
      maski: "Varaajapussimaski (Non-rebreather)",
      virtaus: "10–15 l/min",
      taso: "Korkea O2 (Tajuton)",
      ohje: "Täytä pussi hapella ennen potilaalle asettamista! Jos oma hengitys hiipuu (HT < 10), avusta heti elvytyspalkeella (12–15 l/min).",
    },
    pikaPikaPika: [
      "ILMATIET AUKI HETI JA KYLKIASENTOON: Estää tukehtumisen kieleen ja nieltyyn eritteeseen/oksennukseen (aspiraatio).",
      "MITTAA VERENSOKERI (B-Gluk) VÄLITTÖMÄSTI: Matala sokeri tuhoaa aivot minuuteissa! Jos tajuton: suojaa ilmatiet ja sivele glukoosigeeliä poskeen (ei pistettävää lääkettä).",
      "HENGITYSLAAMAN VALMIUS: Jos hengitystaajuus < 10/min -> avusta heti elvytyspalkeella (Ambu) ja 100 % O2 (12–15 l/min).",
      "ASETA NIELUPUTKI syvästi tajuttomalle, jos sietää sen kakomatta. Anna lisähappi varaajapussimaskilla 10–15 l/min.",
    ],
    safety: [
      "Tarkista vaaratekijät (häkämyrkytys, sähkö, kaasut, lääke/huumeneulat, aggressiivisuus).",
      "Arvioi hätäsiirron tarve turvalliseen tilaan.",
    ],
    mitaKysytaan: [
      "Mitä tapahtui ja miten tajuttomuus alkoi (äkillisesti vai vähitellen)?",
      "Edeltävät oireet: valittiko päänsärkyä, rintakipua, hengenahdistusta tai pahoinvointia?",
      "Oliko kouristelua tai nykimistä?",
      "Perussairaudet: diabetes, epilepsia, sydänsairaus, aivoverenkiertohäiriöt, päihdetausta?",
      "Lääkitykset: insuliini/diabeteslääkkeet, verenohentajat (Marevan ym.), unilääkkeet, rauhoittavat?",
      "Ympäristön tutkiminen: löytyykö lääkepurkkeja, alkoholipulloja, huumeruiskuja tai viestejä?",
    ],
    mitaMitataan: [
      "1. Ilmatien avoimuus ja hengityksen riittävyys: hengitystaajuus (HT / hengityskertaa minuutissa: hätätila aikuisella alle 10 tai yli 25 /min).",
      "2. Happisaturaatio eli veren happipitoisuus (SpO2, aloita lisähappi jos alle 95 %).",
      "3. Syke ja verenpaine (RR) (hätätila aikuisella: syke alle 40 tai levossa yli 140 /min; yläpaine alle 90 mmHg).",
      "4. VERENSOKERI (B-Gluk / sormenpäämittaus) VÄLITTÖMÄSTI – sulje pois ja hoida matala sokeri (hypoglykemia)!",
      "5. Tajunnan taso GCS-pisteinä (Glasgow Coma Scale, SI-PU-LI-4-5-6, 3–15 pistettä):",
      "   - Silmät (SI 1–4 p): 4 spontaani, 3 puheelle, 2 kivulle, 1 ei avaa",
      "   - Puhe (PU 1–5 p): 5 asiallinen, 4 sekava, 3 irrallisia sanoja, 2 ääntelyä, 1 ei mitään",
      "   - Liike (LI 1–6 p): 6 noudattaa, 5 paikantaa kivun, 4 väistää, 3 epänormaali koukistus, 2 ojennus, 1 ei vastetta",
      "6. Pupillit eli silmäterät (koko, symmetria ja reaktio valoon).",
      "7. Kehon lämpötila (korvakuumemittarilla).",
      "8. Ihon lämpötila, kosteus, pistojäljet ja mahdolliset vammat (etenkin pään alue).",
    ],
    mitaTehdaan: [
      "1. Avaa ilmatiet nostamalla leukakulmista pään ollessa neutraaliasennossa.",
      "2. Aseta potilas KYLKIASENTOON mahansisällön keuhkoihin vetämisen (aspiraation) estämiseksi (rankaa tukien, ellei vamma estä).",
      "3. Aseta nielutuubi ilmatien turvaamiseksi, jos potilas on syvästi tajuton ja sietää sen ilman kakomista.",
      "4. Aloita lisähappi: varaajapussimaski 10–15 l/min (täytä pussi hapella ennen asettamista!), jos SpO2 < 95 %.",
      "5. Jos hengitys käy riittämättömäksi tai loppuu -> avusta heti elvytyspalkeella (maskiventilaatio).",
      "6. Jos verensokeri (B-Gluk) on alle 3.0 mmol/l ja potilas tajuton:",
      "   - Aseta potilas ehdottomasti KYLKIASENTOON ja turvaa ilmatiet. Ensivasteella ei ole pistettävää lääkettä (ei Glucagen-pistosta).",
      "   - Sivele varovasti glukoosigeeliä (esim. GlucoBooster) posken sisäpinnalle/limakalvolle siten, ettei geeliä pääse nieluun tai tuki ilmateitä.",
      "   - Konsultoi tarvittaessa lääkäriyksikköä (ESA 00) tai kenttäjohtajaa (L4).",
      "7. Seuraa elintoimintoja ja tajunnan tasoa (GCS) vähintään 5 min välein.",
      "8. Ilmoita tilanne saapuvalle ensihoitoyksikölle iSBAR-mallilla.",
    ],
    erityishuomiot: [
      "Muistisääntö VOI IHME tajuttomuuden syille:",
      "V = Vuoto aivoissa / Vamma (kallovamma)",
      "O = O2-puute (hapenpuute eli asfyksia/hypoksia)",
      "I = Intoksikaatio (myrkytys: lääkkeet, alkoholi, huumeet, myrkkykaasut)",
      "I = Infektio (sepsis eli verenmyrkytys, aivokalvontulehdus)",
      "H = Hypoglykemia (matala verensokeri)",
      "M = Matala verenpaine / Matala ruumiinlämpö (hypotermia)",
      "E = Epilepsia (kouristuskohtauksen jälkitila)",
      "! = Simulaatio / teeskentely",
    ],
  },
  {
    code: "703",
    codes: ["703"],
    title: "Hengitysvaikeus",
    category: "breathing",
    categoryLabel: "Hengitys",
    urgency: "A / B / C",
    shortSummary:
      "Nyrkkisääntö: Jos hengitys on näkyvää, se on vaikeutunutta. Aseta puoli-istuvaan asentoon, rauhoita, anna lisähappea jos veren happipitoisuus (SpO2) < 95 %.",
    happisuositus: {
      maski: "Varaajapussimaski tai Normaali maski",
      virtaus: "10–15 l/min tai 5–8 l/min",
      taso: "SpO2-ohjattu",
      ohje: "Vaikeassa hengenahdistuksessa aina varaajapussimaski 10–15 l/min (täytä pussi ennen asettamista!). Lievemmässä hengenahdistuksessa voidaan käyttää normaalia maskia (5–8 l/min, aina väh. 5 l/min). Keuhkoahtaumataudissa (COPD) tavoite 88–92 %, mutta älä estä happea akuutissa hengenvaarassa.",
    },
    pikaPikaPika: [
      "PUOLI-ISTUVA ASENTO HETI etukumaraan nojaten – älä koskaan pakota hengitysvaikeuspotilasta makuulle!",
      "LISÄHAPPI HETI: Jos SpO2 < 95 % tai apulihastyö, VARAAJAPUSSIMASKI 10–15 l/min (täytä pussi ennen asettamista!).",
      "TUNNISTA UUPUMINEN: Jos puhe lakkaa, tajunta hämärtyy tai HT < 10/min -> ALOITA VÄLITÖN AVUSTETTU VENTILAATIO palkeella (Ambu 100 % O2).",
      "RAUHOITA POTILASTA JA TILANNETTA: Hätääntyminen moninkertaistaa hapenkulutuksen.",
    ],
    safety: [
      "Varmista ettei tilassa ole savua, kaasuja tai muita myrkyllisiä yhdisteitä.",
      "Tarkkaile potilaan uupumista.",
    ],
    mitaKysytaan: [
      "Miten nopeasti hengenahdistus alkoi (sekunneissa/minuuteissa = vierasesine, keuhkoveritulppa, anafylaksia eli allerginen reaktio, ilmarinta; tunneissa/päivissä = keuhkokuume, keuhkoahtaumatauti COPD, sydämen vajaatoiminta)?",
      "Onko rintakipua, yskää, kuumetta tai turvotusta jaloissa?",
      "Perussairaudet: astma, keuhkoahtaumatauti (COPD), sydämen vajaatoiminta, allergiat?",
      "Kotilääkitys: avaavat hengityssuihkeet, nitrot, sydänlääkkeet? Onko ottanut omat lääkkeet ja auttoiko?",
    ],
    mitaMitataan: [
      "1. Hengitystaajuus (HT): laske hengitykset 30 s ajan (normaali aikuisella 12–20, hätätila alle 10 tai yli 25 /min).",
      "2. Happisaturaatio eli veren happipitoisuus (SpO2) ennen lisähapen antoa ja lisähapen aikana.",
      "3. Puhekyky: pystyykö puhumaan kokonaisia lauseita, lyhyitä lauseita, vain yksittäisiä sanoja vai ei lainkaan?",
      "4. Hengitysäänet korvin ja stetoskoopilla kuunnellen: vinkuna (astma/ahtauma), rohina (keuhkopöhö), toispuolinen hiljaisuus (ilmarinta/keuhkokuume).",
      "5. Rannesyke (taajuus ja laatu; syke usein yli 120 /min vaikeassa hengenahdistuksessa).",
      "6. Verenpaine (RR), verensokeri (B-Gluk) ja kehon lämpötila.",
      "7. Ihon väri: ihon sinertävyys hapenpuutteen vuoksi (syanoosi), kalpeus, hikisyys.",
    ],
    mitaTehdaan: [
      "1. Aseta potilas PUOLI-ISTUVAAN asentoon etukumaraan nojaten (helpottaa apuhengityslihasten työtä).",
      "   - HUOM: Jos rannesyke ei tunnu, kohota sekä jalat että ylävartalo!",
      "2. Rauhoita potilasta – paniikki ja puhuminen pahentavat hengenahdistusta.",
      "3. Lisähappi SpO2-tason ja potilaan tilanteen mukaan:",
      "   - Aloita lisähappi varaajapussimaskilla 10–15 l/min (täytä pussi hapella ennen asettamista!), jos SpO2 < 95 % tai potilaalla on apulihastyötä.",
      "   - Keuhkoahtaumatautipotilas (COPD / tunnettu hiilidioksidiretentio): Tavoitesaturaatio 88–92 %, mutta älä panttaa happea akuutissa hengenvaarassa!",
      "4. Jos potilas alkaa uupua, tajunnantaso laskee tai hengitystaajuus HT < 10 -> avusta hengitystä elvytyspalkeella (Ambu) ja 100 % lisähapella.",
      "5. Seuraa vasteita ja ilmoita tilanne iSBAR-mallilla kohteeseen saapuvalle hoitoyksikölle.",
    ],
    erityishuomiot: [
      "Hengitysvaikeuden vaikeusasteen pika-arvio:",
      "- Lievä: Hengitystaajuus (HT) 20–25, veren happi (SpO2) > 92 %, puhuu kokonaisia lauseita.",
      "- Kohtalainen: HT 25–30, SpO2 85–92 %, puhuu lyhyitä lauseita, apuhengityslihakset hieman käytössä.",
      "- Vaikea: HT 30–40, SpO2 70–85 %, puhuu vain yksittäisiä sanoja, apulihakset voimakkaasti käytössä, kylmänhikinen, tiheä syke (takykardia > 120 /min).",
      "- Kriittinen: HT > 40 tai < 10, SpO2 < 70 %, iho sinertävä hapenpuutteesta (syanoottinen), sekava/uupunut, ei jaksa istua ilman tukea -> välitön elvytysvalmius!",
    ],
  },
  {
    code: "704",
    codes: ["704"],
    title: "Rintakipu",
    category: "circulation",
    categoryLabel: "Sydän & Verenkierto",
    urgency: "A / B",
    shortSummary:
      "Rintakipu on suuren vaaran oire! Puoli-istuva lepoasento. ASA 250 mg pureskeltavaksi. Dinit-sumute jos yläpaine (RR) > 100 mmHg ja syke > 50.",
    happisuositus: {
      maski: "Normaali maski tai Varaajapussimaski",
      virtaus: "5–8 l/min tai 10–15 l/min",
      taso: "Tarpeen mukaan (SpO2 < 95 %)",
      ohje: "Anna lisähappea vain jos SpO2 < 95 % (tavoite ≥ 95 %). Lievään hypoksiaan normaali maski 5–8 l/min (aina väh. 5 l/min). Vaikeammassa hypoksiassa varaajapussimaski 10–15 l/min. Tarpeeton liikahapetus supistaa sepelvaltimoita.",
    },
    pikaPikaPika: [
      "TÄYSI FYYSINEN LEPO HETI: Älä anna ottaa askeleitakaan kävelyä (minimoi sydänlihaksen hapentarve). Puoli-istuva asento.",
      "ASA 250 mg PURESKELTAVAKSI välittömästi (estää veritulpan kasvua sepelvaltimossa, ellei ASA-allergiaa).",
      "DINIT-SUUSUMUTE (2 suihketta kielen päälle): VAIN jos yläpaine (RR) > 100 mmHg, syke > 50 /min EIKÄ erektiolääkkeitä käytetty 48h sisällä!",
      "AED VIERELLE VALMIIKSI ja elvytysvalmius (kammiovärinäriski suurin ensi minuutteina!). Lisähappi maskilla VAIN jos SpO2 < 95 %.",
    ],
    safety: [
      "Potilasta ei saa kävelyttää tai rasittaa fyysisesti lainkaan!",
      "Rauhoita tilanne ja ympäristö.",
    ],
    mitaKysytaan: [
      "Kivun alkamisajankohta ja mitä teki kivun alkaessa (levossa vai rasituksessa)?",
      "Kivun luonne: puristava, ahdistava, painava, vannemainen? (Sopiiko sydänperäiseksi?)",
      "Kivun säteily: leuka, kaula, vasen tai molemmat olkavarret, selkä lapojen väliin, ylävatsa?",
      "Kivun voimakkuus kipumittarilla (VAS 0–10, missä 0 = kivuton, 10 = pahin kuviteltavissa oleva kipu)?",
      "Muuttuuko kipu asennon tai hengityksen mukaan? (Pistävä/tarkkarajainen voi olla lihas/keuhkoperäinen, mutta sydäninfarkti suljettava aina ensin pois!)",
      "Liitännäisoireet: hengenahdistus, kylmänhiki, pahoinvointi, heikotus?",
      "Perussairaudet: sepelvaltimotauti, aiempi sydäninfarkti, verenpainetauti, diabetes?",
      "Lääkkeet: onko ottanut omaa nitroa (montako suihketta/tablettia, auttoiko)?",
      "TÄRKEÄÄ: Onko käytetty erektiolääkkeitä (Viagra, Cialis tms.) viimeisen 24–48 tunnin aikana? -> EHDOTON VASTA-AIHE NITRAATEILLE (aiheuttaa hengenvaarallisen verenpaineen romahduksen)!",
    ],
    mitaMitataan: [
      "1. Verenpaine (RR) – mittaa molemmista käsistä jos mahdollista (suuri puoliero voi viitata aortan repeämään eli aorttadissekaatioon).",
      "2. Syke (taajuus, tasaisuus/rytmi, laatu).",
      "3. Happisaturaatio eli veren happipitoisuus (SpO2).",
      "4. Hengitystaajuus (HT) ja ihon tila (harmaan kalpea, kylmän hikinen viittaa sydäninfarktiin).",
      "5. Verensokeri (B-Gluk) ja kehon lämpötila.",
      "6. Kivun voimakkuus kipuasteikolla (VAS 0–10, seuraa 5 minuutin välein).",
    ],
    mitaTehdaan: [
      "1. Aseta potilas täyteen lepoon puoli-istuvaan asentoon (tai makuulle jos verenpaine on matala).",
      "2. Rauhoita potilasta ja kevennä kiristäviä vaatteita.",
      "3. LISÄHAPPI: Aloita lisähappi normaalilla happimaskilla (5–8 l/min) tai varaajapussimaskilla (10–15 l/min) VAIN jos veren happipitoisuus (SpO2) on alle 95 % (tarpeeton lisähappi supistaa sepelvaltimoita).",
      "4. LÄÄKEHOITO (koulutuksen ja yksikön oikeuksien mukaisesti):",
      "   a) ASA (Asetyylisalisyylihappo, Aspirin/Disperin):",
      "      - Annos: 250 mg (puolikas 500 mg tabletista) PURESKELTAVAKSI (estää verihiutaleiden takertumista ja tulpan kasvua).",
      "      - Vasta-aihe: tunnettu yliherkkyys asetyylisalisyylihapolle (ASA-allergia).",
      "   b) DINIT-suusumute (isosorbididinitraatti 1.25 mg/annos, laajentaa verisuonia ja helpottaa sydämen työtä):",
      "      - EHDOT: Systolinen verenpaine (yläpaine) yli 100 mmHg JA syke yli 50 /min.",
      "      - Annostelu: 2 suihkausta kielen päälle 30 sekunnin välein.",
      "      - Voidaan toistaa 3 minuutin kuluttua, jos rintakipu jatkuu ja verenpaine- sekä sykerajat täyttyvät.",
      "      - HUOM: Jos yläpaine (systolinen RR) laskee alle 100 mmHg, aseta potilas heti makuulle ja nosta jalat koholle!",
      "5. Ilmoita potilaan tilanne ja annettu lääkehoito kohteeseen tulevalle hoitoyksikölle iSBAR-mallilla.",
    ],
    erityishuomiot: [
      "Diabeetikoilla, iäkkäillä ja naisilla sydäninfarktin oireisto voi olla poikkeava: ei kovaa rintakipua, vaan pelkkää heikotusta, hengenahdistusta, hikoilua tai epämääräistä ylävatsakipua!",
    ],
  },
  {
    code: "705",
    codes: ["705"],
    title: "Äkillisesti heikentynyt yleistila / Rytmihäiriö",
    category: "general",
    categoryLabel: "Yleistila & Rytmi",
    urgency: "A / B / C",
    shortSummary:
      "Kartoita kehitysnopeus. Sulje pois vakavat syyt: verenmyrkytys (sepsis), sydäninfarkti tai aivoverenkiertohäiriö. ABCDE-arvio ja täysi lepo.",
    happisuositus: {
      maski: "Normaali maski tai Varaajapussimaski",
      virtaus: "5–8 l/min tai 10–15 l/min",
      taso: "Tarpeen mukaan",
      ohje: "Anna lisähappea vain jos SpO2 < 95 % tai potilas on sokissa. Lievään hypoksiaan normaali maski 5–8 l/min (aina väh. 5 l/min), sokissa tai vaikeassa tilassa varaajapussimaski 10–15 l/min.",
    },
    pikaPikaPika: [
      "TUNNUSTELE SYKE JA MITTAA VERENPAINE: Selvitä välittömästi onko potilas sokissa (nopea heikko syke, yläpaine < 90 mmHg).",
      "MITTAA HETI VERENSOKERI (B-Gluk) JA LÄMPÖTILA: Poissulje hypoglykemia ja hengenvaarallinen verenmyrkytys (sepsis).",
      "SOKKIASENTO: Jos verenpaine romahtanut -> potilas makuulle ja jalat ylös. Jos tajunta heikkenee -> käännä heti kylkiasentoon.",
      "LISÄHAPPI: Varaajapussimaski 10–15 l/min jos SpO2 < 95 % tai potilas on sokissa.",
    ],
    safety: [
      "Tarkista potilaan kaatumis- ja loukkaantumisriski.",
      "Avaa kiristävät vaatteet.",
    ],
    mitaKysytaan: [
      "Miten nopeasti vointi on heikentynyt (tunneissa vai päivissä)?",
      "Mikä on potilaan pääasiallinen vaiva ja tuntemus?",
      "Onko ollut rintakipua, tykytystuntemusta tai hengenahdistusta?",
      "Onko ollut ohimenevää tajunnan menetystä tai huimausta seistessä?",
      "Infektio-oireet: kuume, vilunväristykset, yskä, virtsaamisvaivat, ripuli tai oksentelu?",
      "Perussairaudet ja kotilääkitys (sydänlääkkeet, diabetes, antibiootit)?",
      "Onko aiemmin ollut vastaavaa yleistilan romahdusta?",
    ],
    mitaMitataan: [
      "1. Syke ja sydämen rytmi (onko tasainen, epätasainen, nopea yli 140 vai hidas alle 40 /min?).",
      "2. Verenpaine (RR) molemmista käsistä tarvittaessa.",
      "3. Verensokeri (B-Gluk) heti!",
      "4. Kehon lämpötila korvakuumemittarilla (kuume tai alilämpöisyys eli hypotermia).",
      "5. Happisaturaatio eli veren happi (SpO2) ja hengitystaajuus (HT).",
      "6. Tajunnan taso GCS-pisteinä (orientoituminen aikaan, paikkaan, henkilöön).",
      "7. Ääreisverenkierron tila: ihon lämpötila, lämpöraja, marmorointi (laikukas iho heikon verenkierron merkkinä) ja kapillaaritäyttöaika (väri kynnenaluseen alle 2 s).",
    ],
    mitaTehdaan: [
      "1. Aseta potilas lepoon mukavaan asentoon (puoli-istuva tai makuu).",
      "2. Rauhoita ja lämmitä potilas tarvittaessa peittein.",
      "3. Anna lisähappea, jos veren happipitoisuus (SpO2) on alle 95 %.",
      "4. Jos tajunnan taso alenee -> käännä kylkiasentoon ja turvaa ilmatiet.",
      "5. Jos potilaalla on vakavan verenmyrkytyksen (sepsis) merkit (kuume/vilunväreet, matala verenpaine RR, tiheä hengitys HT > 22, sekavuus) -> raportoi kiireellisesti.",
      "6. Raportoi potilaan tila iSBAR-mallilla tulevalle ensihoitoyksikölle.",
    ],
    erityishuomiot: [
      "Äkillinen yleistilan heikkeneminen ilman selvää syytä voi olla oire vakavasta piilevästä sairaudesta (esim. sepsis eli verenmyrkytys, keuhkoveritulppa tai oireeton 'hiljainen' sydäninfarkti).",
    ],
  },
  {
    code: "706",
    codes: ["706"],
    title: "Aivohalvaus (AVH / Stroke / TIA)",
    category: "neurological",
    categoryLabel: "Neurologia",
    urgency: "A / B",
    shortSummary:
      "Aika on aivoa! Selvitä heti tarkka oireiden alkamisaika. Älä kävelytä potilasta. Tee FPSS-aivohalvaustesti (0–8 p, FAST-testi) ja mittaa verensokeri (B-Gluk).",
    happisuositus: {
      maski: "Normaali maski tai Varaajapussimaski",
      virtaus: "5–8 l/min tai 10–15 l/min",
      taso: "Tarpeen mukaan (SpO2 < 95 %)",
      ohje: "Anna lisähappea vain jos SpO2 < 95 % (tavoite 95–98 %). Lievään hypoksiaan normaali maski 5–8 l/min (aina väh. 5 l/min), vaikeammassa hypoksiassa varaajapussimaski 10–15 l/min. Vältä tarpeetonta liikahapetusta ilman hypoksiaa.",
    },
    pikaPikaPika: [
      "ÄLÄ KÄVELYTÄ TAI NOSTA PYSTYYN: Suuri kaatumis- ja lisävammavaara! Lepoon puoli-istuvaan asentoon (pää 30° koholla).",
      "TARKKA OIREIDEN ALKAMISAIKA kellonlyömältä ylös: Liuotushoito ja tukoksen poisto (trombektomia) ovat minuuttipeliä!",
      "MITTAA VERENSOKERI (B-Gluk) HETI: Matala sokeri matkii aivohalvausta täydellisesti.",
      "FPSS- / FAST-AIVOHALVAUSTESTI (0–8 p): Kasvot (1p), Raajat (1p), Puhe (1p), Näkö (1p) ja Katseen kääntö (4p). 1–4 p = liuotushoito, 5–8 p = suuren suonen tukos (LVO) / trombektomia! ÄLÄ laske korkeaa verenpainetta!",
    ],
    safety: [
      "ÄLÄ KÄVELYTÄ TAI NOSTA POTILASTA SEISOMAAN (tasapainon ja lihasvoiman äkillinen menetys).",
      "Lepoon vuoteeseen tai puoli-istuvaan asentoon.",
    ],
    mitaKysytaan: [
      "TARKKA OIREIDEN ALKAMISAIKA (kellonaika) tai milloin potilas on viimeksi nähty täysin oireettomana?",
      "Miten oire havaittiin ja kuka sen huomasi?",
      "Potilaan aikaisempi toimintakyky (omatoiminen, liikkuva, muistisairas)?",
      "Käytössä olevat lääkkeet: erityisesti verenohennuslääkkeet (Marevan, Eliquis, Xarelto, Pradaxa, Lixiana)?",
      "Perussairaudet: aiempi aivoinfarkti tai ohimenevä aivoverenkiertohäiriö (TIA), eteisvärinä, verenpainetauti, diabetes?",
    ],
    mitaMitataan: [
      "1. FPSS-aivohalvausmittari (Finnish Prehospital Stroke Scale, 0–8 p):",
      "   - Kasvot (1 p): toinen suupieli roikkuu / epäsymmetrinen.",
      "   - Raajojen voima (1 p): toispuolinen heikkous, käsi/jalka vajoaa kannattelukokeessa.",
      "   - Puhe (1 p): epäselvä, sammaltava (dysartria) tai sanojen löytämisen/ymmärtämisen vaikeus (afasia).",
      "   - Näkö (1 p): toisen puolen näkökenttäpuutos tai kaksoiskuvat.",
      "   - Katseen tai pään kääntyminen (4 p): katseen/pään pakkoasento sivulle poispäin halvauksesta -> VAHVA SUUREN SUONEN TUKOKSEN (LVO) MERKKI!",
      "2. VERENSOKERI (B-Gluk) VÄLITTÖMÄSTI – matala sokeri (hypoglykemia) matkii aivohalvausta täydellisesti!",
      "3. FAST-pikatesti: Kasvot (Face), Kädet (Arms), Puhe (Speech), Aika (Time).",
      "4. Pupillit eli silmäterät (koot ja valoreaktiot) sekä tajunnan taso (GCS-laskuri).",
      "5. Verenpaine (RR) – HUOM: Aivohalvauspotilaan korkeaa verenpainetta ei saa laskea ilman lääkärin lupaa! Jos yläpaine laskee alle 120 mmHg -> nosta jalat ylös aivoverenkierron turvaamiseksi.",
      "6. Syke ja sydämen rytmi (eteisvärinän havainnointi).",
      "7. Happisaturaatio eli veren happipitoisuus (SpO2, aloita lisähappi normaalilla maskilla 5–8 l/min tai varaajapussimaskilla vain jos SpO2 < 95 %) ja lämpötila.",
    ],
    mitaTehdaan: [
      "1. Aseta potilas lepoon puoli-istuvaan asentoon (pää koholla n. 30°), tajuton potilas kylkiasentoon.",
      "2. Vältä nieluputkea jos potilas reagoi (yskimisrefleksi nostaa haitallisesti aivopainetta!).",
      "3. Valmistele nopea raportointi ja kuljetus: jokainen minuutti ratkaisee veritulpan liuotushoidon ja mekaanisen poiston (trombektomian) onnistumisen!",
      "4. Ota välitön yhteys kohteeseen tulevaan hoitoyksikköön / lääkäriin (ESA 00 / L4) ja anna tiedot (alkamisaika, FPSS-pisteet 0–8 p ja löydökset, B-Gluk, verenohennuslääkkeet). Jos FPSS ≥ 5 p, huomioi suora kuljetus trombektomiakeskukseen!",
    ],
    erityishuomiot: [
      "Äkillinen, ennennäkemättömän kova päänsärky ('kuin salama kirkkaalta taivaalta') voi viitata lukinkalvonalaiseen aivoverenvuotoon (SAV / subaraknoidaalivuoto)!",
    ],
  },
  {
    code: "711",
    codes: ["711", "713", "714"],
    title: "Hapenpuute / Ilmatie-este / Hukkuminen",
    category: "breathing",
    categoryLabel: "Hengitys",
    urgency: "A",
    shortSummary:
      "Ilmatie-este: 5 iskua lapaluiden väliin + 5 vatsanpainallusta (Heimlich). Hukkunut: elvytys aloitetaan heti 5 puhalluksella!",
    happisuositus: {
      maski: "Varaajapussimaski tai Elvytyspalje",
      virtaus: "12–15 l/min",
      taso: "100 % O2 (Kriittinen)",
      ohje: "Maksimaalinen happi heti. Jos hengitys on riittämätöntä tai potilas eloton, avusta välittömästi elvytyspalkeella (hukkuneelle 5 alkupuhallusta).",
    },
    pikaPikaPika: [
      "VIERASESINE / TUKEHTUMINEN: 5 napakkaa iskua lapaluiden väliin + 5 vatsanykäisyä (Heimlich). Jos menee tajuttomaksi -> heti lattialle ja ALOITA PPE 30:2!",
      "HUKKUNUT: ALOITA HETI 5 ALKUPUHALLUKSELLA! Elvytyksen laatu ratkaisee, syynä aina hypoksia.",
      "MAKSIMAALINEN LISÄHAPPI: Varaajapussimaski tai elvytyspalje (12–15 l/min 100 % O2).",
      "ESTÄ JÄÄHTYMINEN (hypotermia): Poista märät vaatteet, kuivaa ja peittele lämpimästi. Käsittele hellävaroen (rytmihäiriöriski).",
    ],
    safety: [
      "Vesipelastuksessa ja rannalla oma työturvallisuus (pelastusliivit, turvaköydet).",
      "Varo alilämpöisen (hypotermisen) potilaan rytmihäiriöitä liikuteltaessa.",
    ],
    mitaKysytaan: [
      "Mitä potilas teki oireiden alkaessa (ruokailu, lapsilla pienten esineiden käsittely)?",
      "Kuinka äkillisesti oireet alkoivat?",
      "Hukkumistapauksessa:",
      "- Veden lämpötila ja kauanko potilas oli veden alla?",
      "- Nähtiinkö tapahtuma vai löydettiinkö vedestä?",
      "- Oliko kyseessä hyppy veteen (epäile aina kaularankavammaa!)?",
      "- Annettiinko maallikkoelvytystä ja kuinka kauan?",
    ],
    mitaMitataan: [
      "1. Ilmatien avoimuus ja ilmavirtaus suusta ja nenästä.",
      "2. Hengitysliikkeet ja hengitysäänet: vinkuva sisäänhengitysääni (stridor) tukehtumisen merkkinä.",
      "3. Happisaturaatio (SpO2) ja pulssi.",
      "4. Tajunnan taso GCS-pisteinä.",
      "5. Kehon lämpötila (jäähtymisen eli hypotermian asteen arviointi hukkuneella).",
    ],
    mitaTehdaan: [
      "VIERASESINEEN POISTO (Tajuissaan oleva aikuinen tai yli 1-vuotias lapsi):",
      "1. Jos potilas yskii tehokkaasti: kehota jatkamaan yskimistä, älä tee mekaanisia toimenpiteitä!",
      "2. Jos yskiminen on tehotonta tai potilas tukehtuu:",
      "   - Taivuta potilaan vartaloa eteenpäin ja anna 5 napakkaa iskua lapaluiden väliin kämmentyvellä.",
      "   - Jos ei auta: tee 5 vatsanpohjan nykäisyä eli Heimlichin otetta (seiso takana, nyrkki rintalastan alaosan ja navan väliin, nopea nykäisy sisään ja ylös).",
      "   - Toista vuorotellen 5 iskua selkään ja 5 nykäisyä vatsalle.",
      "3. Pieni vauva (alle 1v): aseta vauva vatsalleen käsivarrelle pää alaspäin, lyö 5 kertaa selkään, käännä selälleen ja tee 5 rintalastapainallusta kahdella sormella.",
      "4. Jos potilas menee TAJUTTOMAKSI:",
      "   - Aseta potilas lattialle, soita lisäapu ja aloita painelu-puhalluselvytys (PPE 30:2).",
      "   - Kurkista suuhun ennen jokaista puhallusyritystä – poista vierasesine vain jos se näkyy selvästi (ei sokeaa kaivelua!).",
      "   - Ventiloi elvytyspalkeella (Ambu) ja 100 % lisähapella.",
      "HUKKUMISEN ENSIHOITO:",
      "1. Jos eloton: ALOITA ELVYTYS 5 ALKUPUHALLUKSELLA, jatka normaalia painelu-puhalluselvytystä (PPE) ja kiinnitä sydäniskuri (AED).",
      "2. Poista märät vaatteet, kuivaa iho ja peittele lämpimästi lämpöpeittein tai avaruuslakanoin.",
      "3. Käsittele potilasta varoen – alilämpöinen (hypoterminen) sydän on erittäin herkkä kammiovärinälle.",
    ],
    erityishuomiot: [
      "Alilämpöinen (hypoterminen) hukkunut ei ole kuollut ennen kuin hänet on lämmitetty ja todettu kuolleeksi sairaalassa!",
    ],
  },
  {
    code: "740",
    codes: ["741", "744", "745", "746", "747"],
    title: "Vammautuminen / Trauma",
    category: "trauma",
    categoryLabel: "Vammat & Trauma",
    urgency: "A / B",
    shortSummary:
      "Tyrehdyttäkää suuret ulkoiset vuodot välittömästi! Kaularangan tuenta käsin + kauluri. Tutki potilas RiVaLAiSeR-järjestyksessä. Estä jäähtyminen (hypotermia).",
    happisuositus: {
      maski: "Varaajapussimaski (Non-rebreather)",
      virtaus: "10–15 l/min",
      taso: "Korkea O2 (Trauma)",
      ohje: "Suurienergisessä vammassa tai verenvuodossa aina varaajapussimaski 10–15 l/min kudoshapenpuutteen ja sokin estämiseksi.",
    },
    pikaPikaPika: [
      "cABCDE - TYREHDYTÄ HENKEÄ UHKAAVA VERENVUOTO HETI: Kiristysside tai paineside raajaan, suora voimakas painaminen!",
      "TUE KAULARANKA KÄSIN neutraaliasentoon heti – älä päästä otetta ennen tukikaulurin ja sivutukien asennusta!",
      "RUNSAS LISÄHAPPI: Suurienergisessä vammassa aina varaajapussimaski 10–15 l/min (estää kudoshypoksian ja sokin).",
      "ESTÄ HYPOTERMIA (jäähtyminen): Peittele potilas välittömästi lämpöpeittein ja avaruuslakanoin – jäähtyminen romahduttaa veren hyytymisen!",
    ],
    safety: [
      "Varmista onnettomuuspaikan turvallisuus (liikenne, sortumat, lasi, sähkö, vuotavat polttoaineet).",
      "Käytä suojavaatteita, suojalaseja ja viiltosuojakäsineitä.",
      "Arvioi vamma-energia: suurienerginen vamma -> aina lisähappi ja rankatuki!",
    ],
    mitaKysytaan: [
      "Mitä tapahtui: vammamekanismi, putoamiskorkeus, törmäysnopeus, turvavyön käyttö, kypärä?",
      "Menettikö potilas tajuntansa missään vaiheessa?",
      "Missä tuntuu kipua, arkuutta, pistelyä tai puutumista?",
      "Pystyvätkö raajat liikkumaan normaalisti?",
      "Perussairaudet ja lääkitykset (erityisesti verenohennuslääkkeet!).",
      "Viimeisin ruokailu tai juominen (leikkausvalmiuden arviointi sairaalassa).",
    ],
    mitaMitataan: [
      "1. Hengitystaajuus (HT) ja rintakehän liike (HT alle 8 tai yli 30 /min = välitön hengenvaara!).",
      "2. Happisaturaatio eli veren happipitoisuus (SpO2).",
      "3. Syke ja verenpaine (RR) (traumapotilaalla yläpaine 80–90 mmHg riittävä, salliva matala verenpaine eli salliva hypotensio vuodon hillitsemiseksi).",
      "4. Tajunnan taso GCS-pisteinä ja silmäterät (pupillit).",
      "5. Verensokeri (B-Gluk) ja kehon lämpötila.",
      "6. Ääreisverenkierron lämpö, väri ja raajojen pulssit.",
    ],
    mitaTehdaan: [
      "1. cABCDE-toimintamalli:",
      "   - c (catastrophic bleeding / hengenvaarallinen vuoto): Tyrehdytä suuret ulkoiset vuodot välittömästi suoralla painamisella, painesiteellä tai kiristyssiteellä!",
      "   - A (Airway / Ilmatiet): Avaa ilmatie leukaa kohottaen samalla KAULARANKAA KAKSIN KÄSIN TUKIEN (ei pään taivutusta taaksepäin). Aseta tukikauluri varovasti.",
      "   - B (Breathing / Hengitys): Arvioi rintakehän symmetria, murtumat ja ihonalainen ilma (rahina). Suurienergiselle vammalle aina lisähappi varaajapussimaskilla 10–15 l/min!",
      "   - C (Circulation / Verenkierto): Tunnustele sykkeet. Kohota tarvittaessa varoen jalat, ellei lantio- tai raajamurtumia.",
      "   - D (Disability / Tajunta): Tajunnan taso GCS-pisteinä ja neurologiset puutokset.",
      "   - E (Exposure / Paljastaminen ja lämpötalous): Riisu potilas vammojen kartoittamiseksi ja peittele heti lämpimästi jäähtymisen (hypotermian) estämiseksi.",
      "2. Systemaattinen RiVaLAiSeR-tutkimus:",
      "   - Ri (Rintakehä): sivele kylkiluut, etsi epäsymmetriaa tai varstarintaa (hengityksen vastakkaista liikettä).",
      "   - Va (Vatsa): tunnustele kämmenellä edeten kivuttomalta alueelta kipeään, tutki pinkeyttä.",
      "   - L (Lantio): ÄLÄ PAINA KÄSIN! Etsi silmin virheasentoja tai jalan lyhentymää/kiertymää.",
      "   - Ai (Aivot/Kallo/Kasvot/Kaula): tunnustele kallon painumat, haavat, kaularanka.",
      "   - Se (Selkäranka): käännä varoen koko vartaloa rankaa yhtenä pakettina tukien ja tunnustele nikamat.",
      "   - R (Raajat): etsi murtumat, virheasennot, haavat ja tarkista raajojen pulssit, tunto ja liike.",
      "3. Tue murtumat lastoilla ja siirrä potilas tyhjiöpatjalle tai rankalaudalle pääntukineen.",
      "4. Estä hypotermia eli kehon jäähtyminen (lämpöpeitteet, avaruuslakana).",
    ],
    erityishuomiot: [
      "Monipotilastilanteessa käytä START-triage-ensiluokittelua:",
      "- VIHREÄ: Kaikki kävelevät (lievät vammat).",
      "- KELTAINEN: Ei pysty liikkumaan, mutta hengitystaajuus (HT) 10–30, rannesyke tuntuu ja noudattaa kehotuksia.",
      "- PUNAINEN (välitön hoito!): Hengitystaajuus (HT) alle 10 tai yli 30, rannesyke puuttuu tai ei noudata kehotuksia.",
      "- MUSTA: Menehtynyt. Ei hengitä ilmatien avaamisenkaan jälkeen, kaulavaltimon (carotis) syke puuttuu.",
    ],
  },
  {
    code: "752",
    codes: ["751", "752"],
    title: "Myrkytys / Intoksikaatio",
    category: "poisoning",
    categoryLabel: "Myrkytys & Onnettomuus",
    urgency: "A / B",
    shortSummary:
      "Työturvallisuus ensin! ÄLÄ OKSETUTA POTILASTA. Ota talteen lääkepakkaukset ja reseptit. Turvaa ilmatiet ja aseta kylkiasentoon.",
    happisuositus: {
      maski: "Varaajapussimaski",
      virtaus: "10–15 l/min",
      taso: "Korkea O2 (Häkä/myrkytys)",
      ohje: "Häkämyrkytyksessä (CO) aina maksimaalinen 100 % happi varaajapussimaskilla saturaatiosta riippumatta. Lääkemyrkytyksessä varaudu elvytyspalkeella avustamiseen.",
    },
    pikaPikaPika: [
      "ÄLÄ KOSKAAN OKSETUTA MYRKYTYSPOTILASTA: Välitön tukehtumisen ja aspiraatiokuoleman vaara tajunnan aletessa!",
      "HÄKÄ / SAVUKAASU: Aina 100 % lisähappi varaajapussimaskilla 12–15 l/min riippumatta saturaatiolukemasta (pulssioksimetri näyttää väärin normaalia)!",
      "TURVAA ILMATIET: Jos tajunta laskee -> heti KYLKIASENTOON ja nieluputki. Opioideissa varaudu välittömään elvytyspalkeella avustamiseen.",
      "OTA TALTEEN KAIKKI lääkepurkit, ruiskut ja myrkkyastiat sairaalaan vietäväksi. Mittaa verensokeri (B-Gluk).",
    ],
    safety: [
      "Varmista oma työturvallisuus (huumeruiskut, aggressiivinen käytös, häkäkaasu).",
      "Pyydä tarvittaessa poliisin virka-apua ennen kohteeseen menoa.",
    ],
    mitaKysytaan: [
      "Mitä ainetta tai lääkkeitä potilas on ottanut?",
      "Kuinka paljon ainetta on otettu (tablettimäärät, millilitrat)?",
      "Milloin aineet on otettu (tuntimäärä ennen löytymistä)?",
      "Miten aine on otettu (nielty, pistetty, hengitetty)?",
      "Onko mukana alkoholia tai useita eri aineita (sekakäyttö)?",
      "Onko potilaalla aiempaa masennusta, itsetuhoisuutta tai säännöllistä lääkitystä?",
    ],
    mitaMitataan: [
      "1. VERENSOKERI (B-Gluk / sormenpäämittaus) heti!",
      "2. Tajunnan taso GCS-pisteinä tiheästi seurattuna (muutokset voivat olla nopeita lääkemassan imeytyessä).",
      "3. Hengitystaajuus (HT) ja hengityksen laatu: opioidit aiheuttavat vaarallista hengityslamaa (hengitystaajuus HT alle 8 /min!).",
      "4. Happisaturaatio (SpO2), pulssi ja verenpaine (RR).",
      "5. Kehon lämpötila: alilämpöisyys (hypotermia) tai ylilämpöisyys (hypertermia).",
      "6. Pupillit eli silmäterät: pienet neulanpistepupillit viittaavat opioideihin, suuret laajentuneet pupillit piristeisiin tai masennuslääkkeisiin.",
    ],
    mitaTehdaan: [
      "1. MYRKYTYSPOTILASTA EI SAA KOSKAAN OKSETUTTAA: vaarana mahansisällön vetäminen keuhkoihin (aspiraatio) ja tukehtuminen tajunnan aletessa!",
      "2. Tarkista suu: poista näkyvä pillerimassa sormin.",
      "3. Jos tajunta alentunut: turvaa ilmatiet nieluputkella ja käännä KYLKIASENTOON.",
      "4. Jos hengitys lamaantuu -> avusta heti hengitystä elvytyspalkeella (Ambu) ja 100 % lisähapella.",
      "5. Lääkehiili: annetaan vain lääkärin erillisen ohjeen mukaan tajuissaan olevalle potilaalle, jolla ilmatiet turvattu (ei tehoa alkoholeihin, happoihin, emäksiin, rautaan).",
      "6. Kerää talteen KAIKKI kohteesta löytyvät lääkepurkit, läpipainoliuskat, ruiskut ja reseptit sairaalaan vietäväksi.",
      "7. Raportoi löydökset iSBAR-mallilla saapuvalle ambulanssille.",
    ],
    erityishuomiot: [
      "Kaasumyrkytyksessä (koodi 751 häkä tai savukaasut): siirrä potilas välittömästi raittiiseen ilmaan ja anna 100 % happea varaajamaskilla 12–15 l/min!",
    ],
  },
  {
    code: "771",
    codes: ["771"],
    title: "Sokeritasapainon häiriö (Hypo- ja Hyperglykemia)",
    category: "metabolic",
    categoryLabel: "Aineenvaihdunta",
    urgency: "A / B",
    shortSummary:
      "Mittaa heti verensokeri (B-Gluk). Matala sokeri eli hypoglykemia (< 3.9 mmol/l): jos tajuissaan, anna glukoosigeeliä (GlucoBooster) tai nopeaa sokeria. Jos tajuton -> kylkiasento, ilmatiet ja varovasti glukoosigeeliä posken limakalvolle (ei pistettävää lääkettä).",
    happisuositus: {
      maski: "Normaali maski tai Varaajapussimaski",
      virtaus: "5–8 l/min tai 10–15 l/min",
      taso: "Tajunnan ja SpO2:n mukaan",
      ohje: "Anna lisähappea jos SpO2 < 95 %: tajuissaan olevalle normaali maski 5–8 l/min (aina väh. 5 l/min), tajuttomalle varaajapussimaski 10–15 l/min (suojaa ilmatiet kylkiasennossa).",
    },
    pikaPikaPika: [
      "MITTAA VERENSOKERI (B-Gluk) VÄLITTÖMÄSTI: Matala sokeri (< 3.9 mmol/l) vaurioittaa aivoja erittäin nopeasti!",
      "TAJUISSAAN JA PYSTYY NIELEMÄÄN: Anna heti suun kautta GLUKOOSIGEELIÄ (esim. GlucoBooster) tai nopeaa sokeria/mehua.",
      "TAJUTON TAI EI PYSTY NIELEMÄÄN: Aseta heti KYLKIASENTOON ja turvaa ilmatiet. Sivele glukoosigeeliä varovasti posken sisäpinnalle (ei pistettävää lääkettä).",
      "SEURAA VERENSOKERIA ja tajuntaa 5 min välein. Korkeassa sokerissa (> 15 mmol/l) ei insuliinia ensivasteelta.",
    ],
    safety: [
      "Matala verensokeri (hypoglykemia) voi tehdä potilaasta sekavan, ärtyisän tai aggressiivisen – toimi rauhallisesti.",
    ],
    mitaKysytaan: [
      "Onko potilas diabeetikko (tyyppi 1 vai tyyppi 2)?",
      "Milloin potilas on viimeksi syönyt ja ottanut insuliinin tai diabeteslääkkeet?",
      "Onko ollut poikkeavaa fyysistä rasitusta, alkoholin käyttöä tai infektioita?",
      "Miten nopeasti oireet alkoivat: matala sokeri (hypoglykemia) alkaa minuuteissa, korkea sokeri (hyperglykemia) päivissä?",
    ],
    mitaMitataan: [
      "1. VERENSOKERI (B-Gluk / sormenpäämittaus) heti ensiarviossa!",
      "   - Hypoglykemia (matala verensokeri): alle 3.9 mmol/l (vaikeat aivo-oireet alle 2.5–3.0 mmol/l).",
      "   - Hyperglykemia (korkea verensokeri): yli 15.0 mmol/l (epäile happomyrkytystä eli ketoasidoosia).",
      "2. Tajunnan taso GCS-pisteinä.",
      "3. Verenpaine (RR), syke, hengitystaajuus (ketoasidoosissa syvä ja tiheä huokaushengitys eli Kussmaulin hengitys, asetonin haju).",
      "4. Happisaturaatio (SpO2) ja kehon lämpötila.",
      "5. Ihon tila: matalassa sokerissa iho on kylmänhikinen ja kalpea; korkeassa sokerissa iho on kuiva ja lämmin.",
    ],
    mitaTehdaan: [
      "MATALAN VERENSOKERIN HOITO (Hypoglykemia / B-Gluk alle 3.9 mmol/l):",
      "1. Jos potilas on tajuissaan ja pystyy nielemään:",
      "   - Anna suun kautta nopeasti imeytyvää hiilihydraattia: GLUKOOSIGEELIÄ (esim. GlucoBooster), tuoremehua, sokerijuomaa tai sokerinpaloja.",
      "2. Jos potilas on tajuton, sekava tai ei kykene turvallisesti nielemään (B-Gluk alle 3.0–3.9 mmol/l):",
      "   - Aseta potilas ehdottomasti KYLKIASENTOON ja turvaa vapaat ilmatiet.",
      "   - Huomio: Ensivasteella EI ole pistettävää lääkettä (ei Glucagen-injektiota).",
      "   - Sivele varovasti GLUKOOSIGEELIÄ (esim. GlucoBooster) posken sisäpinnalle/limakalvolle siten, ettei geeli pääse nieluun tai aiheuta aspiraatiovaaraa.",
      "   - Konsultoi tarvittaessa ensihoitolääkäriä (ESA 00) tai kenttäjohtajaa (L4).",
      "   - Seuraa verensokeria ja hengitystä jatkuvasti ambulanssin saapumiseen asti.",
      "KORKEAN VERENSOKERIN HOITO (Hyperglykemia / B-Gluk yli 15 mmol/l):",
      "1. Ensivaste EI anna insuliinia.",
      "2. Aseta potilas lepoon, turvaa peruselintoiminnot ja anna lisähappea tarvittaessa.",
      "3. Seuraa arvoja ja raportoi iSBAR-mallilla hoitotasolle.",
    ],
    erityishuomiot: [
      "Diabeetikon tajuttomuus voi johtua myös muusta kuin sokerista (esim. aivoverenvuoto, vakava infektio). Mittaa sokeri (B-Gluk) aina!",
    ],
  },
  {
    code: "772",
    codes: ["772"],
    title: "Kouristelu",
    category: "neurological",
    categoryLabel: "Neurologia",
    urgency: "A / B",
    shortSummary:
      "Poissulje ensin sydänpysähdys! ÄLÄ ESTÄ KOURISTELUA MEKAANISESTI. Suojaa pää. Anna lisähappea ja mittaa verensokeri (B-Gluk).",
    happisuositus: {
      maski: "Varaajapussimaski",
      virtaus: "10–15 l/min",
      taso: "Korkea O2 (Kouristus)",
      ohje: "Kouristuskohtauksen aikana ja heti sen jälkeen runsas lisähappi varaajapussimaskilla hypoksian ehkäisemiseksi. Kylkiasento ja leuan nosto.",
    },
    pikaPikaPika: [
      "ÄLÄ ESTÄ KOURISTELUA VOIMALLA äläkä laita mitään väkisin potilaan suuhun hampaiden väliin!",
      "SUOJAA PÄÄ pehmeällä esineellä (takki, tyyny) ja siirrä vaaralliset esineet kauemmas.",
      "KOHTAUKSEN LAANNUTTUA: Käännä heti KYLKIASENTOON, avaa ilmatiet, anna lisähappea varaajamaskilla (10–15 l/min) ja MITTAA VERENSOKERI (B-Gluk).",
      "STATUS EPILEPTICUS: Jos kouristus kestää > 5 min tai toistuu ilman heräämistä -> välitön hätätila ja kiireellinen raportti hoitotasolle!",
    ],
    safety: [
      "Suojaa potilas loukkaantumiselta (siirrä kovat ja terävät esineet kauemmas).",
      "Älä laita mitään väkisin potilaan suuhun hampaiden väliin!",
    ],
    mitaKysytaan: [
      "Kouristuksen alku, kesto ja tyyppi (vartalon jäykistys, raajojen nykiminen)?",
      "Onko potilas toipunut kohtausten välillä vai toistuvatko kouristukset ilman heräämistä (pitkittynyt kouristustila eli status epilepticus)?",
      "Aiempi epilepsia, aivokasvain, aivovamma tai aivoverenkiertohäiriö?",
      "Diabetes (matalan verensokerin aiheuttama kouristus)?",
      "Päihteiden käyttö tai alkoholin äkillinen lopettaminen (vieroituskouristus eli viinakramppi)?",
      "Lapsella äkillinen kuumeen nousu (kuumekouristus)?",
      "Raskaana olevalla raskausmyrkytys (pre-eklampsia tai eklampsia)?",
    ],
    mitaMitataan: [
      "1. Sydänpysähdyksen poissulku: sydänpysähdyksen alkukouristelu loppuu nopeasti ja potilas jää elottomaksi ilman elonmerkkejä -> jos eloton, aloita elvytys (PPE) heti!",
      "2. VERENSOKERI (B-Gluk / sormenpäämittaus) – mittaa heti kun kouristelu hellittää.",
      "3. Happisaturaatio (SpO2) ja hengitystaajuus (HT, hengitys voi lamaantua kohtauksen aikana).",
      "4. Verenpaine (RR) ja sydämen syke.",
      "5. Kehon lämpötila (etenkin lapset kuumekouristuksessa).",
      "6. Tajunnan taso (GCS-seuranta) kohtauksen jälkeisestä syvästä uneliaisuudesta (jälkiunesta) toipumisen aikana.",
    ],
    mitaTehdaan: [
      "1. Kouristeluvaiheessa:",
      "   - ÄLÄ yritä estää kouristelua voimalla tai mekaanisesti pitelemällä.",
      "   - Suojaa potilaan pää pehmeällä esineellä (esim. takki tai tyyny).",
      "   - Pidä ilmatiet avoimina ja anna lisähappea maskilla lähelle kasvoja.",
      "2. Kouristuksen loputtua:",
      "   - Käännä potilas heti KYLKIASENTOON (nielun eritteiden ja kielen taaksevalumisen estämiseksi).",
      "   - Avaa ilmatiet, puhdista suu tarvittaessa ja aseta nielutuubi jos sietää.",
      "   - Anna lisähappea varaajamaskilla (10–15 l/min, SpO2 tavoite > 95 %).",
      "   - Mittaa verensokeri (B-Gluk) ja hoida mahdollinen matala sokeri (hypoglykemia).",
      "3. Rauhoita läsnäolijat – kouristuskohtaus on usein omaisille pelottava kokemus.",
      "4. Jos kouristelu kestää yli 5 minuuttia tai toistuu ilman heräämistä -> HÄTÄTILA, informoi tulevaa hoitoyksikköä välittömästi.",
    ],
    erityishuomiot: [
      "Kohtauksen jälkeen potilas on usein unelias, sekava ja muistamaton (jälkiunioireisto). Seuraa toipumista rauhassa kylkiasennossa.",
    ],
  },
  {
    code: "773",
    codes: ["773"],
    title: "Allerginen reaktio & Anafylaksia",
    category: "allergic",
    categoryLabel: "Allergia",
    urgency: "A / B",
    shortSummary:
      "Anafylaksiassa (hengitysvaikeus + verenpaineen lasku): ADRENALIINI HETI LIHAKSEEN (EpiPen 0.3 mg / Jr 0.15 mg). 100 % happi ja puoli-istuva tai sokkiasento.",
    happisuositus: {
      maski: "Varaajapussimaski",
      virtaus: "10–15 l/min",
      taso: "Korkea O2 (Anafylaksia)",
      ohje: "Korkea happivirtaus heti EpiPenin rinnalla! Valmistaudu ilmateiden turpoamiseen ja elvytyspalkeella avustamiseen.",
    },
    pikaPikaPika: [
      "ADRENALIINI HETI LIHAKSEEN (EpiPen 0.3 mg / Jr 0.15 mg) reiden ulkosyrjään – älä viivyttele jos hengitys vinkuu, nielu turpoaa tai verenpaine laskee!",
      "100 % LISÄHAPPI HETI varaajapussimaskilla 10–15 l/min. Varaudu nopeaan ilmatieturvotukseen.",
      "ASENTOHOITO: Hengitysvaikeudessa puoli-istuva asento. Verenpaineen romahtaessa (sokki) makuulle ja jalat ylös!",
      "LOPETA ALTISTUS (poista mehiläisen piikki raapaisemalla puristamatta). Raportoi välittömästi hoitoyksikölle.",
    ],
    safety: [
      "Varmista, ettei altistus jatku (poista ampiainen tai mehiläisen piikki raapaisemalla, älä purista myrkkypussia).",
      "Huomioi hoitovälineiden allergiat (esim. lateksihanskat!).",
    ],
    mitaKysytaan: [
      "Mille potilas on altistunut (ruoka-aine, pähkinä, lääke, hyönteisen pisto)?",
      "Kuinka nopeasti oireet alkoivat altistuksesta (mitä nopeampi alku, sitä vaarallisempi reaktio)?",
      "Aiempien allergisten reaktioiden vakavuus?",
      "Onko potilas jo ottanut omia lääkkeitä (antihistamiini, kyypakkaus/kortisoni, EpiPen-adrenaliini)?",
    ],
    mitaMitataan: [
      "1. Ilmatien turvotus: äänen käheys, vinkuva sisäänhengitysääni (stridor), nielemisvaikeus.",
      "2. Hengitystyö, hengitystaajuus (HT) ja happisaturaatio (SpO2).",
      "3. Verenpaine (RR) ja syke (verenpaineen lasku ja nopea syke eli takykardia ovat anafylaktisen sokin merkkejä!).",
      "4. Iholöydökset: laaja paukamainen nokkosihottuma (urtikaria), punoitus, huulten ja silmäluomien turvotus.",
      "5. Vatsaoireet: kouristava vatsakipu, oksentelu, ripuli.",
      "6. Tajunnan taso GCS-pisteinä.",
    ],
    mitaTehdaan: [
      "1. Lopeta altistus välittömästi.",
      "2. Asentohoito:",
      "   - Hengitysvaikeudessa: puoli-istuva asento.",
      "   - Jos verenpaine romahtaa tai rannesyke katoaa (sokki): makuulle ja alaraajat koholle.",
      "3. Anna 100 % lisähappea varaajamaskilla (10–15 l/min).",
      "4. ANAFYLAKSIASSA (henkeä uhkaava äkillinen yliherkkyysreaktio):",
      "   - ANNA ADRENALIINI välittömästi reisilihakseen ulkosivulle (i.m.):",
      "     * Aikuinen: EpiPen 0.3 mg (300 mikrog) autoinjektori.",
      "     * Lapsi (15–30 kg): EpiPen Jr 0.15 mg (150 mikrog) autoinjektori.",
      "   - Antotapa: Napauta injektori n. 10 cm etäisyydeltä lujasti reiden ulkosyrjään ja pidä paikallaan 5–10 sekuntia.",
      "5. Ota välitön yhteys tulevaan hoitoyksikköön iSBAR-mallilla.",
      "6. Varaudu toistamaan adrenaliini 5–15 minuutin kuluttua lääkärin ohjeen mukaan, jos tila ei korjaannu.",
    ],
    erityishuomiot: [
      "Vakavan allergisen reaktion saanut potilas kuuluu AINA sairaalahoitoon seurantaan, vaikka oireet helpottaisivat kohteessa (reaktion kaksivaiheisuus eli oireiden uusiutuminen tuntien kuluttua!).",
    ],
  },
  {
    code: "791",
    codes: ["791"],
    title: "Synnytys",
    category: "obstetric",
    categoryLabel: "Synnytys",
    urgency: "A / B",
    shortSummary:
      "Arvioi ehtiikö sairaalaan (supistukset < 5 min tai ponnistamisen tarve -> kohteessa syntymä). Kuivaa ja peittele vastasyntynyt lämpimästi iholle. Älä vedä napanuorasta!",
    happisuositus: {
      maski: "Normaali maski tai Varaajapussimaski (äiti)",
      virtaus: "5–8 l/min tai 10–15 l/min",
      taso: "Tarpeen mukaan",
      ohje: "Äidille lisähappea tarvittaessa (SpO2 < 95 %): normaali maski 5–8 l/min (aina väh. 5 l/min) tai varaajapussimaski 10–15 l/min. Vastasyntynyt: kuivaa ja lämmitä; jos ei virkoa, aloita elvytys huoneilmalla / palkeella ohjeen mukaan.",
    },
    pikaPikaPika: [
      "ARVIOI EHTIIKÖ SAIRAALAAN: Jos supistusväli < 5 min, ponnistamisen tarve tai pää näkyy -> SYNNYTYS HOIDETAAN KOHTEESSA, älä lähde ajamaan!",
      "KULJETUSASENTO: Äiti AINA vasemmalle kyljelle (estää alaonttolaskimon puristumisen ja verenpaineen romahtamisen).",
      "VASTASYNTYNYT: KUIVAA HETI JA PEITTELE LÄMPIMÄSTI äidin iholle – suojaa pää lämpöhukalta! ÄLÄ vedä napanuorasta.",
      "JOS VAUVA EI HENGITÄ: Stimuloi jalkapohjista ja selästä. Ellei virkoa -> aloita elvytys huoneilmalla/palkeella (n. 50 puhallusta/min).",
    ],
    safety: [
      "Rauhoitu ja rauhoita äiti ja tukihenkilöt.",
      "Valmistele lämpimät puhtaat pyyhkeet, peitot ja imulaite.",
    ],
    mitaKysytaan: [
      "Monesko raskaus ja synnytys on kyseessä?",
      "Raskausviikot ja laskettu aika (täysiaikainen synnytys yli 37 raskausviikkoa)?",
      "Supistusten tiheys, kesto ja alkamisaika?",
      "Onko lapsivesi mennyt ja oliko se kirkasta vai vihreää/veristä?",
      "Tunteeko äiti voimakasta ponnistamisen tarvetta tai peräsuolen painetta?",
      "Raskauden kulku ja neuvolalöydökset (monisikiöisyys, eteisistukka)?",
    ],
    mitaMitataan: [
      "Äiti:",
      "- Verenpaine (RR), syke ja happisaturaatio (SpO2) ennen synnytystä.",
      "- Synnytyksen jälkeen kohdun verenvuodon määrä.",
      "Vastasyntynyt:",
      "- Hengitystaajuus (HT / terveellä vauvalla yli 30 /min).",
      "- Syke (terveellä vauvalla yli 100 /min tunnusteltuna napanuoran tyvestä tai kuunneltuna stetoskoopilla).",
      "- Ihon väri, lihasjäntevyys ja reagointi (itkeekö vauva äänekkäästi?).",
    ],
    mitaTehdaan: [
      "1. ARVIOI EHTIIKÖ SAIRAALAAN:",
      "   - Jos uudelleensynnyttäjä ja supistusväli alle 5 min TAI ponnistamisen tarve TAI lapsen pää näkyy synnytyskanavassa -> EI EHDITÄ, synnytys hoidetaan kohteessa!",
      "   - Jos ei vielä synny ja kuljetus aloitetaan: äiti aina vasemmalle kyljelle (estää alaonttolaskimon puristumisen eli supiinioireyhtymän ja verenpaineen romahduksen).",
      "2. SYNNYTYKSEN HOITO KOHTEESSA:",
      "   - Tue vauvan päätä varovasti kämmenellä synnytyskanavasta ulos tullessa, älä vedä!",
      "   - Kirjaa syntymän tarkka kellonaika muistiin.",
      "3. VASTASYNTYNEEN HOITO:",
      "   - Nosta lapsi heti äidin vatsalle/rinnalle iho ihoa vasten.",
      "   - KUIVAA LAPSI HUOLELLISESTI ja vaihda kuivaan lämpimään peitteeseen – suojaa erityisesti vauvan pää lämpöhukalta!",
      "   - Terveen lapsen hengitysteitä EI tarvitse rutiinisti imeä.",
      "   - Jos lapsi ei hengitä kunnolla tai syke alle 60 /min:",
      "     * Stimuloi taputtelemalla jalkapohjiin ja hieromalla selkää 30–60 s ajan.",
      "     * Jos ei virkoa -> aloita puhalluselvytys (hartioiden alle pieni pyyherulla, 100 % O2 maskilla n. 50 krt/min).",
      "     * Jos ei sykettä -> paineluelvytys kahdella sormella 100/min rytmillä 3:1.",
      "4. ISTUKKA JA JÄLKEISVAIHE (istukan syntyminen):",
      "   - Istukka syntyy yleensä n. 15–30 min kuluessa vauvasta. ÄLÄ VEDÄ NAPANUORASTA!",
      "   - Anna vauvan imeä rintaa (vapauttaa oksitosiini-hormonia, edistää kohdun supistumista ja hillitsee verenvuotoa).",
      "   - Jos kohdusta tulee runsasta verenvuotoa: hiero ja paina kohtua päältä kaksin käsin alavatsalta ja anna äidille lisähappea.",
    ],
    erityishuomiot: [
      "Hätäkuljetus sairaalaan välittömästi, jos emättimestä tarjoutuu käsi tai jalka (virhetarjonta) tai napanuora valahtaa lapsen edelle!",
    ],
  },
  {
    code: "030",
    codes: ["030", "031", "032", "033", "034"],
    title: "Väkivalta (Ampuminen, puukotus, pahoinpitely)",
    category: "violence",
    categoryLabel: "Väkivalta",
    urgency: "A / B",
    shortSummary:
      "Oma työturvallisuus ensin – poliisi kohteeseen ensimmäisenä! Säästä rikostekniset jäljet ja vaatteiden reiät. Tyrehdytä heti vuodot.",
    happisuositus: {
      maski: "Varaajapussimaski",
      virtaus: "10–15 l/min",
      taso: "Korkea O2 (Vamma)",
      ohje: "Vuotosokin ja rintakehävammojen riski: runsas lisähappi varaajapussimaskilla 10–15 l/min.",
    },
    pikaPikaPika: [
      "OMA TYÖTURVALLISUUS ENSIJAINEN: Varmista että poliisi on kohteessa ennen ensivasteen menoa! Jätä perääntymistie auki.",
      "TYREHDYTÄ HENKEÄ UHKAAVAT VERENVUODOT VÄLITTÖMÄSTI: Kiristysside raajaan, paineside tai suora voimakas painaminen haavaan.",
      "RINTAKEHÄN PISTOVAMMASSA peitä haava ilmatiiviisti/venttiilisiteellä (estä hengenvaarallinen paineilmarinta). Varaajapussimaski 10–15 l/min.",
      "RIKOSJÄLJET: Leikkaa vaatteet vain saumoista – ÄLÄ KOSKAAN LEIKKAA luodin- tai teränreikien läpi! Säilytä kaikki poliisille.",
    ],
    safety: [
      "TYÖTURVALLISUUS ON ENSISIJAISTA:",
      "- Varmista jo matkalla: onko poliisi hälytetty ja kohteessa?",
      "- Onko tekijä poistunut vai paikalla? Onko aseita?",
      "- Älä astu suoraan oviaukkoon, jätä aina vapaa perääntymistie!",
      "- Käytä suojakäsineitä ja suojalaseja – varo verikontaktia (veriteitse tarttuvat taudit).",
    ],
    mitaKysytaan: [
      "Miten väkivalta on tehty (teräase, ampuma-ase, potkut, lyönnit, tylppä esine)?",
      "Monenko iskun tai laukauksen kohteeksi potilas joutui?",
      "Millainen tekoväline oli (terän pituus, luodin kaliiperi jos tiedossa)?",
      "Menettikö potilas tajuntansa tapahtumahetkellä?",
      "Perussairaudet ja verenohennuslääkitys?",
    ],
    mitaMitataan: [
      "1. Hengitystaajuus (HT) ja ilmatien avoimuus.",
      "2. Happisaturaatio (SpO2), verenpaine (RR) ja syke (ääreisverenkierron tila ja verenvuotosokin oireet).",
      "3. Tajunnan taso GCS-pisteinä ja silmäterät (pupillit).",
      "4. Vammojen tarkka etsintä: kainalot, nivuset, selkä, pakarat, päänahka.",
      "5. Ampumahaavoissa: etsi aina sekä luodin sisäänmeno- että ulostuloreiät!",
    ],
    mitaTehdaan: [
      "1. cABCDE: TYREHDYTÄ VERENVUODOT VÄLITTÖMÄSTI painamalla suoraan haavaan, painesiteellä tai kiristyssiteellä.",
      "2. ÄLÄ SORKI HAAVAA sormella tai millään välineellä.",
      "3. Rintakehän lävistävä haava: peitä ilmatiiviillä sidoksella tai venttiilisiteellä (varo hengenvaarallisen paineilmarinnan kehittymistä keuhkopussiin!).",
      "4. Jos potilas tajuton ja ilmatiet uhattuna -> kylkiasentoon.",
      "5. OIKEUDELLISET ASIAT JA RIKOSJÄLKIEN SÄILYTYS:",
      "   - Liiku rikospaikalla mahdollisimman vähän.",
      "   - Jos leikkaat vaatteita, LEIKKAA SAUMOJEN MUKAAN – ÄLÄ koskaan leikkaa luodin- tai puukonreikien läpi!",
      "   - Älä hävitä mitään roskiin, säilytä kaikki potilaan vaatteet ja esineet poliisille.",
      "6. Raportoi tilanne iSBAR-mallilla saapuvalle hoitotasolle.",
      "7. Tehtävän jälkeen pidä purkukeskustelu ja henkinen jälkipuinti (defusing-istunto) ryhmän kanssa.",
    ],
    erityishuomiot: [
      "Teräaseen aiheuttama haava voi ulkoisesti näyttää pieneltä, mutta syvällä voi olla hengenvaarallinen valtimovuoto tai sisäelinvaurio!",
    ],
  },
];

export const ENSIVASTE_LAAKKEET = [
  {
    name: "Adrenaliini (EpiPen® / EpiPen Jr®)",
    dose: "Aikuinen 0.3 mg (300 µg), Lapsi (15–30 kg) 0.15 mg (150 µg)",
    route: "Lihakseen (i.m. / intramuskulaarisesti) reiden ulkosivulle",
    indications: "Anafylaktinen sokki ja hengenvaarallinen allerginen reaktio.",
    effects:
      "Supistaa verisuonia, nostaa verenpainetta, laajentaa keuhkoputkia ja vähentää limakalvoturvotusta.",
    contraindications: "Hätätilanteessa ei ole vasta-aiheita.",
    notes: "Pidä injektoria reittä vasten 5–10 sekuntia.",
  },
  {
    name: "ASA (Asetyylisalisyylihappo, Aspirin® / Disperin®)",
    dose: "250 mg (puolikas 500 mg tabletista)",
    route: "Pureskeltavaksi suun kautta",
    indications:
      "Sydänperäiseksi epäilty puristava rintakipu / sepelvaltimokohtaus.",
    effects:
      "Estää verihiutaleiden takertumista toisiinsa (verihiutaleiden aggregaatiota) ja veritulpan kasvua sepelvaltimossa.",
    contraindications:
      "Tunnettu yliherkkyys asetyylisalisyylihapolle (ASA-allergia).",
    notes: "Pureskellaan nopean imeytymisen takaamiseksi.",
  },
  {
    name: "Dinit® suusumute (Isosorbididinitraatti)",
    dose: "2 suihketta (1.25 mg/annos) kielen päälle 30 s välein",
    route: "Suusumute kielen päälle",
    indications: "Sydänperäinen puristava rintakipu.",
    effects:
      "Laajentaa laskimoita ja sepelvaltimoita, keventää sydämen työmäärää.",
    contraindications:
      "Systolinen verenpaine (yläpaine) alle 100 mmHg, syke alle 50 /min, erektiolääkkeiden käyttö (24–48 h).",
    notes:
      "Voidaan uusia 3 min kuluttua jos kipu jatkuu ja verenpaine > 100 mmHg. Jos verenpaine laskee alle 100 mmHg -> makuulle ja jalat ylös!",
  },
  {
    name: "Glukoosigeeli (esim. GlucoBooster®)",
    dose: "1 tuubi / annospussi (voidaan uusia vasteen mukaan)",
    route: "Suun kautta tai posken sisäpinnalle (limakalvolle)",
    indications:
      "Diabeetikon matala verensokeri eli hypoglykemia (B-Gluk alle 3.9 mmol/l).",
    effects:
      "Nostaa verensokeritasoa nopeasti imeytymällä suun limakalvolta ja ruoansulatuskanavasta.",
    contraindications:
      "Korkea verensokeri (hyperglykemia / B-Gluk yli 15 mmol/l).",
    notes:
      "Tajuissaan oleva ja nielemään kykenevä potilas ottaa geelin itse suun kautta. Jos potilas on tajuton tai sekava, aseta ehdottomasti KYLKIASENTOON ja sivele geeliä varovasti posken sisäpinnalle (limakalvolle) siten, ettei geeliä joudu hengitysteihin. Ensivasteella EI ole pistettäviä sokerilääkkeitä (ei Glucagen-pistosta).",
  },
  {
    name: "Lääkkeellinen happi (O2)",
    dose: "5–8 l/min (normaali maski), 10–15 l/min (varaajapussimaski) tai 12–15 l/min (elvytyspalje)",
    route: "Inhalaatio (maski) tai ventilaatio (palje)",
    flowRate:
      "Ensivasteen maskit ja virtausnopeudet:\n• Elvytyspalje varaajapussilla: 12–15 l/min (elottomuus ja avustettu ventilaatio, jopa 100 % O2)\n• Varaajapussimaski (Non-rebreather): 10–15 l/min (korkea O2-pitoisuus 60–90 %, vaikea hengenahdistus, kriittinen trauma, tajuton, häkä, anafylaksia, sokki — täytä pussi aina ennen kasvoille asettamista!)\n• Normaali happimaski (yksinkertainen maski): 5–8 l/min (kohtalainen lisähapentarve, SpO2 < 95 %, vakaa rintakipu- tai AVH-potilas — virtaus aina vähintään 5 l/min CO2-kertymisen estämiseksi)",
    maskType:
      "Ensivasteen käytössä olevat maskit ja välineet:\n1. Elvytyspalje (Ambu) varaajapussilla: elottomuus (700/701), hengityspysähdys tai riittämätön hengitys (HT < 10)\n2. Varaajapussimaski (Non-rebreather): korkea happipitoisuus kriittisissä tilanteissa ja vaikeassa hapenpuutteessa (10–15 l/min)\n3. Normaali happimaski: lievässä ja kohtalaisessa lisähapentarpeessa vakaammalle potilaalle (5–8 l/min, aina väh. 5 l/min)",
    indications:
      "Hypoksia (SpO2 < 95 %), elvytys, vaikea vamma, anafylaksia, häkämyrkytys (aina 100 % O2 varaajapussimaskilla).",
    effects:
      "Parantaa kudosten ja elinten hapensaantia, ehkäisee soluvaurioita ja elinvaurioita.",
    contraindications:
      "Hätätilanteessa ei vasta-aiheita! Tunnetussa keuhkoahtaumataudissa (COPD) tavoite SpO2 88–92 %, mutta älä koskaan estä hapensaantia akuutissa hengenvaarassa.",
    notes:
      "Ensivasteessa käytetään maskeja tai paljetta (ei happiviiksiä). Normaalia happimaskia käytettäessä virtausnopeuden tulee olla AINA vähintään 5 l/min (5–8 l/min), jottei uloshengitettyä hiilidioksidia kerry maskiin. Varaajapussimaskia käytettäessä täytä pussi aina hapella ennen potilaalle asettamista.",
  },
];

export const ISBAR_MALLI = {
  title: "iSBAR-konsultaatio- ja raportointimalli",
  description:
    "Yhtenäinen raportointi ensihoidon kenttäjohtajalle (L4) tai lääkäriyksikölle (ESA 00 / lääkärihelikopteri).",
  steps: [
    {
      letter: "i",
      name: "Identify (Tunnistautuminen)",
      content:
        "Oma nimi, hoidollinen taso (ensivaste), yksikön tunnus (esim. RSA 51), paikkakunta sekä potilaan nimi, ikä ja henkilötunnus.",
    },
    {
      letter: "S",
      name: "Situation (Tilanne)",
      content:
        "Miksi soitetaan, tilanteen vakavuus ja mitä hoito-ohjetta tai vahvistusta pyydetään.",
    },
    {
      letter: "B",
      name: "Background (Tausta)",
      content:
        "Tapahtumatiedot, potilaan oireet, oireiden alkamisaika, perussairaudet, säännöllinen lääkitys ja lääkeaineallergiat.",
    },
    {
      letter: "A",
      name: "Assessment (Nykytilanne / ABCDE)",
      content:
        "A+B (ilmatie, hengitystaajuus HT, veren happisaturaatio SpO2, hengitysäänet), C (syke, verenpaine RR, rytmi, ihon väri ja lämpö), D (tajunnan taso GCS, verensokeri B-Gluk, silmäterät/pupillit), E (kehon lämpö, vammat, annettu ensihoito ja vaste).",
    },
    {
      letter: "R",
      name: "Recommendation (Toimintaehdotus & Kertaus)",
      content:
        "Oma työdiagnoosi ja toimenpide-ehdotus. Saadun hoito-ohjeen kuittaaminen toistamalla se ääneen (suljettu silmukka).",
    },
  ],
};

export const GCS_CRITERIA = {
  eyes: [
    {
      score: 4,
      title: "Spontaani",
      desc: "Aukaisee silmät ilman ärsykettä",
      label: "Spontaani (aukaisee silmät ilman ärsykettä)",
    },
    {
      score: 3,
      title: "Puheelle",
      desc: "Aukaisee silmät kehotuksesta / puheesta",
      label: "Puheelle (aukaisee silmät kehotuksesta)",
    },
    {
      score: 2,
      title: "Kivulle",
      desc: "Aukaisee silmät kipuärsykkeelle",
      label: "Kivulle (aukaisee silmät kipuärsykkeelle)",
    },
    {
      score: 1,
      title: "Ei vastetta",
      desc: "Ei aukaise silmiään millekään",
      label: "Ei vastetta (ei aukaise silmiään)",
    },
  ],
  verbal: [
    {
      score: 5,
      title: "Orientoitunut",
      desc: "Tietää ajan, paikan ja itsensä (asiallinen)",
      label: "Orientoitunut / asiallinen (tietää ajan, paikan ja itsensä)",
    },
    {
      score: 4,
      title: "Sekava",
      desc: "Keskustelee, mutta on sekava / desorientoitunut",
      label: "Sekava (keskustelee, mutta on sekava/desorientoitunut)",
    },
    {
      score: 3,
      title: "Irrallisia sanoja",
      desc: "Epätarkoituksenmukaisia yksittäisiä sanoja",
      label: "Irrallisia sanoja (epätarkoituksenmukaisia sanoja)",
    },
    {
      score: 2,
      title: "Ääntelyä",
      desc: "Ääntelyä, mutinaa tai vaikerointia, ei sanoja",
      label: "Ääntelyä (ääntelyä/vaikerointia, ei sanoja)",
    },
    {
      score: 1,
      title: "Ei mitään",
      desc: "Ei minkäänlaista äänteellistä vastetta",
      label: "Ei mitään (ei äänteellistä vastetta)",
    },
  ],
  motor: [
    {
      score: 6,
      title: "Noudattaa kehotuksia",
      desc: "Nostaa kätensä pyydettäessä / puristaa kättä",
      label: "Noudattaa kehotuksia (nostaa kätensä pyydettäessä)",
    },
    {
      score: 5,
      title: "Paikantaa kivun",
      desc: "Vie kätensä kipukohtaan",
      label: "Paikantaa kivun (vie kätensä kipukohtaan)",
    },
    {
      score: 4,
      title: "Väistää kivun",
      desc: "Koukistaa normaalisti pois kipukohdasta",
      label: "Väistää kivun (koukistaa normaalisti pois kivusta)",
    },
    {
      score: 3,
      title: "Epänormaali koukistus",
      desc: "Fleksio kivulle (dekortikaatio)",
      label: "Epänormaali koukistus / fleksio kivulle",
    },
    {
      score: 2,
      title: "Ojennus",
      desc: "Ekstensio kivulle (deserebraatio)",
      label: "Ojennus / ekstensio kivulle",
    },
    {
      score: 1,
      title: "Ei vastetta",
      desc: "Täysin veltto / ei liikettä",
      label: "Ei vastetta (täysin veltto)",
    },
  ],
};

/**
 * Finnish Prehospital Stroke Scale (FPSS)
 * Suomalaisessa ensihoidossa ja ensivastetoiminnassa käytettävä aivohalvausmittari.
 * Kokonaispisteet: 0–8 p.
 * Katseen tai pään suunnan poikkeama (deviaatio) antaa 4 pistettä (suuren suonitukoksen LVO-merkki).
 */
export const FPSS_CRITERIA = {
  face: {
    key: "face",
    step: 1,
    title: "Suupieli / Kasvot",
    short: "Kasvot",
    desc: "Kasvojen symmetria – pyydä potilasta näyttämään hampaat tai hymyilemään.",
    maxScore: 1,
    options: [
      {
        score: 0,
        title: "Normaali (0 p)",
        desc: "Molemmat puolet liikkuvat symmetrisesti, ei puolieroa",
        label: "Normaali (ei puolieroa)",
      },
      {
        score: 1,
        title: "Puoliero (1 p)",
        desc: "Toinen suupieli roikkuu, epäsymmetrinen liike tai kasvohermohalvaus",
        label: "Puoliero (suupieli roikkuu)",
      },
    ],
  },
  arms: {
    key: "arms",
    step: 2,
    title: "Raajojen voima",
    short: "Raajat",
    desc: "Raajojen kannattelukoe: molemmat kädet eteen 10 sekunniksi kämmenet ylöspäin (tai jalkojen nosto).",
    maxScore: 1,
    options: [
      {
        score: 0,
        title: "Normaali (0 p)",
        desc: "Molemmat raajat pysyvät ylhäällä yhtä vahvasti ilman vajoamista",
        label: "Normaali (raajat kannattavat)",
      },
      {
        score: 1,
        title: "Heikkous / Puoliero (1 p)",
        desc: "Toinen käsi tai jalka vajoaa, kiertyy sisäänpäin tai ei nouse lainkaan",
        label: "Heikkous (raaja vajoaa/halvaus)",
      },
    ],
  },
  speech: {
    key: "speech",
    step: 3,
    title: "Puhe",
    short: "Puhe",
    desc: "Kielellinen vaste: pyydä toistamaan lause ('aurinko paistaa') ja nimeämään esineitä.",
    maxScore: 1,
    options: [
      {
        score: 0,
        title: "Normaali (0 p)",
        desc: "Puhe on sujuvaa, selkeää ja ymmärtää ohjeet normaalisti",
        label: "Normaali (puhe selkeää)",
      },
      {
        score: 1,
        title: "Puheen häiriö (1 p)",
        desc: "Sammaltava/puuroutuva puhe (dysartria), sanojen löytämisen vaikeus tai ymmärtämättömyys (afasia)",
        label: "Puheen häiriö (dysartria / afasia)",
      },
    ],
  },
  vision: {
    key: "vision",
    step: 4,
    title: "Näkö",
    short: "Näkö",
    desc: "Näkökenttäpuutos tai kaksoiskuvat: kysy näöstä ja testaa sormilla molemmat näkökentät.",
    maxScore: 1,
    options: [
      {
        score: 0,
        title: "Normaali (0 p)",
        desc: "Ei näköhäiriöitä, näkee normaalisti molemmilta puolilta eikä näe kahtena",
        label: "Normaali (ei näköhäiriötä)",
      },
      {
        score: 1,
        title: "Näkökenttäpuutos tai kaksoiskuvat (1 p)",
        desc: "Toisen puolen näkökenttä sokea (hemianopsia) tai kahtena näkeminen (diplopia)",
        label: "Näköhäiriö (näkökenttäpuutos / kaksoiskuvat)",
      },
    ],
  },
  gaze: {
    key: "gaze",
    step: 5,
    title: "Katseen tai pään suunnan poikkeama (Deviaatio)",
    short: "Katse",
    desc: "Katseen kääntyminen sivulle – tarkkaile silmien ja pään asentoa levossa ja pyydä katsomaan sivuille.",
    maxScore: 4,
    options: [
      {
        score: 0,
        title: "Ei todeta (0 p)",
        desc: "Silmät ja pää liikkuvat normaalisti molempiin suuntiin ilman pakkoasentoa",
        label: "Ei todeta (normaali katse)",
      },
      {
        score: 4,
        title: "Katseen tai pään kääntyminen sivulle (4 p)",
        desc: "Pysyvä tai osittainen hakeutuminen sivulle poispäin halvauksesta (kohti aivovauriota). Vahva suuren suonitukoksen (LVO) merkki!",
        label: "Katseen/pään deviaatio (4 p – LVO-merkki)",
      },
    ],
  },
};

/**
 * Palauttaa FPSS-pisteiden (0–8 p) operatiivisen tulkinnan ja ensivasteen toimenpidesuositukset.
 */
export function interpretFpss(score) {
  const num = Number(score) || 0;
  if (num >= 5) {
    return {
      score: num,
      severity: "critical",
      severityBgClass: "red lighten-5",
      severityBorderClass: "red--border",
      severityTextClass: "red--text text--darken-4",
      severityChipColor: "red darken-4",
      severityIcon: "mdi-alert-octagon",
      severityIconColor: "red darken-4",
      tag: "5–8 p: SUUREN SUONEN TUKOSEPÄILY (LVO) – TROMBEKTOMIAKANDIDAATTI",
      title:
        "Vahva epäily suuren aivosuonen tukoksesta (LVO, Large Vessel Occlusion)",
      subTitle: "Trombektomiakandidaatti (mekaaninen tukoksen poisto)",
      summary:
        "Katseen kääntyminen (4 p) tai useat yhtäaikaiset neurologiset puutosoireet viittaavat suuren aivovaltimon tukokseen. Potilas hyötyy ensisijaisesti mekaanisesta trombektomiasta.",
      actionSteps: [
        "VÄLITÖN ILMOITUS ENSIHOIDOLLISELLE KONSULTAATIOLLE (ESA 00 / L4): Ilmoita FPSS-pisteet " +
          num +
          "/8 p ja vahva LVO-epäily.",
        "KULJETUS SUORAAN TROMBEKTOMIAKESKUKSEEN (esim. TYKS / Tays): Älä viivyttele kohteessa!",
        "TARKKA OIREIDEN ALKAMISAIKA (tai milloin viimeksi nähty täysin oireettomana) kellonlyömältä ylös.",
        "MITTAA VERENSOKERI (B-Gluk) heti – sulje pois hypoglykemia.",
        "LEPOASENTO: Pää koholla n. 30°. ÄLÄ KOSKAAN KÄVELYTÄ potilasta kaatumis- ja lisävammavaaran vuoksi.",
        "ÄLÄ LASKE KORKEAA VERENPAINETTA: Korkea paine suojaa aivoverenkiertoa ja aivokudosta.",
      ],
    };
  } else if (num >= 1) {
    return {
      score: num,
      severity: "warning",
      severityBgClass: "orange lighten-5",
      severityBorderClass: "orange--border",
      severityTextClass: "orange--text text--darken-4",
      severityChipColor: "orange darken-3",
      severityIcon: "mdi-alert-circle",
      severityIconColor: "orange darken-3",
      tag: "1–4 p: AIVOHALVAUSEPÄILY – LIUOTUSHOITOKANDIDAATTI",
      title: "Aivoverenkiertohäiriöepäily (AVH / Aivoinfarkti)",
      subTitle:
        "Ensisijainen liuotushoitokandidaatti (suonensisäinen trombolyysi)",
      summary:
        "Neurologinen puutosoire ilman katseen deviaatiota. Potilas on ensisijainen liuotushoitokandidaatti. Liuotuksen aikaikkuna on rajallinen (yleensä enintään 4,5 h oireiden alusta).",
      actionSteps: [
        "ILMOITA TULEVALLE YKSIKÖLLE: Aivohalvaushälytys, FPSS " +
          num +
          "/8 p.",
        "SELVITÄ TARKKA OIREIDEN ALKAMISAIKA: Jokainen minuutti ratkaisee liuotuksen onnistumisen!",
        "MITTAA VERENSOKERI (B-Gluk) heti.",
        "LEPOASENTO: Pää koholla n. 30°. ÄLÄ kävelytä potilasta.",
        "SELVITÄ VERENOHENNUSLÄÄKKEET: Marevan, Eliquis, Xarelto, Pradaxa, Lixiana (vaikuttavat liuotushoitopäätökseen).",
        "ÄLÄ LASKE VERENPAINETTA ilman lääkärin määräystä.",
      ],
    };
  } else {
    return {
      score: 0,
      severity: "normal",
      severityBgClass: "green lighten-5",
      severityBorderClass: "green--border",
      severityTextClass: "green--text text--darken-4",
      severityChipColor: "green darken-2",
      severityIcon: "mdi-check-circle",
      severityIconColor: "green darken-2",
      tag: "0 p: EI TYYPILLISIÄ FPSS-LÖYDÖKSIÄ",
      title: "Normaali FPSS-löydös (0 p)",
      subTitle:
        "Ei puolieroja, puhehäiriötä, näkökenttäpuutosta tai katseen deviaatiota",
      summary:
        "FPSS-mittarin kohdissa ei todeta puolieroja. Huomaa, että 0 pistettä ei täysin poissulje aivoverenkiertohäiriötä (esim. takakierron infarkti, huimaus, ataksia tai ohimenevä TIA).",
      actionSteps: [
        "HUOMIOI TIA-MAHDOLLISUUS: Oireet ovat voineet jo korjaantua (ohimenevä aivoverenkiertohäiriö vaatii aina sairaalatutkimukset).",
        "Arvioi potilaan tajunnan taso (GCS-laskuri), tasapaino ja kävelykyky.",
        "Mittaa peruselintoiminnot: B-Gluk, RR, SpO2, syke ja lämpötila.",
        "Konsultoi ensihoitoa / lääkäriä mikäli oireita tai epäilyä esiintyy.",
      ],
    };
  }
}

/**
 * Hakee annetulle koodille parhaiten vastaavan ohjeen.
 * Tukee muotoja '704', '704 A', '704B', '031', jne.
 */
export function getOhjeByCode(code) {
  if (!code) return null;
  const str = String(code).trim().toLowerCase();
  if (str.includes("lapsi") || str === "700_lapsi") {
    return ENSIVASTE_OHJEET.find((o) => o.code === "700_lapsi");
  }
  const clean = str.replace(/[^0-9]/g, "");

  // 1. Tarkka osuma suoraan koodeista
  let found = ENSIVASTE_OHJEET.find((o) => o.codes && o.codes.includes(clean));
  if (found) return found;

  // 2. Jos koodi alkaa 700 tai 701 (elvytys/eloton)
  if (clean.startsWith("700") || clean.startsWith("701")) {
    return ENSIVASTE_OHJEET.find((o) => o.code === "700");
  }

  // 3. Jos koodi alkaa 74 (vammautuminen)
  if (clean.startsWith("74")) {
    return ENSIVASTE_OHJEET.find((o) => o.code === "740");
  }

  // 4. Jos koodi alkaa 03 (väkivalta)
  if (clean.startsWith("03")) {
    return ENSIVASTE_OHJEET.find((o) => o.code === "030");
  }

  // 5. Jos koodi alkaa 71 (hapenpuute)
  if (clean.startsWith("71")) {
    return ENSIVASTE_OHJEET.find((o) => o.code === "711");
  }

  // 6. Jos koodi alkaa 75 (myrkytys/onnettomuus)
  if (clean.startsWith("75")) {
    return ENSIVASTE_OHJEET.find((o) => o.code === "752");
  }

  // 7. Yleinen etuliitehaku 3 merkillä
  if (clean.length >= 3) {
    const prefix3 = clean.substring(0, 3);
    found = ENSIVASTE_OHJEET.find((o) => o.codes && o.codes.includes(prefix3));
    if (found) return found;
  }

  return null;
}

/**
 * Tarkistaa onko koodi ensivastekoodi (EVY-koodi)
 */
export function isEvyCode(code) {
  if (!code) return false;
  const clean = String(code)
    .trim()
    .replace(/[^0-9]/g, "");
  if (clean.startsWith("7")) return true; // 700-799 ensihoidon koodit
  if (clean.startsWith("03")) return true; // väkivalta
  if (["483", "484", "485", "486", "487"].includes(clean)) return true; // ihmisen pelastaminen
  return false;
}

/**
 * Lääkehapen suositukset tehtäväluokan vakavuuden mukaan.
 * Selkeä ja nopea noudattaa kohteessa.
 */
export const HAPPI_KATEGORIAT = {
  resuscitation: {
    id: "resuscitation",
    categoryLabel: "Elottomuus & Elvytys (700, 701)",
    maski: "Elvytyspalje (Ambu) varaajalla",
    virtaus: "12–15 l/min",
    taso: "100 % O2 (Elvytys)",
    ohje: "Kaksin käsin tiivis C-E -maskipito 30:2. Oman kierron palattua (ROSC) vaihto varaajapussimaskiin (10–15 l/min).",
  },
  critical: {
    id: "critical",
    categoryLabel: "Kriittinen A-tehtävä (Tajuton, Trauma, Anafylaksia, Häkä)",
    maski: "Varaajapussimaski (Non-rebreather)",
    virtaus: "10–15 l/min",
    taso: "Korkea O2 (60–90 %)",
    ohje: "Täytä pussi hapella ennen potilaalle asettamista! Jos oma hengitys hiipuu (HT < 10), avusta heti elvytyspalkeella.",
  },
  breathing: {
    id: "breathing",
    categoryLabel: "Hengitysvaikeus (703)",
    maski: "Varaajapussimaski tai Normaali maski",
    virtaus: "10–15 l/min tai 5–8 l/min",
    taso: "SpO2-ohjattu",
    ohje: "Vaikeassa hengenahdistuksessa aina varaajapussimaski 10–15 l/min (täytä pussi ennen asettamista!). Lievemmässä hengenahdistuksessa normaali maski 5–8 l/min (aina väh. 5 l/min). Keuhkoahtaumataudissa (COPD) tavoite 88–92 %.",
  },
  moderate: {
    id: "moderate",
    categoryLabel: "Kohtalainen B-tehtävä (Rintakipu, AVH, Sokeri, Yleistila)",
    maski: "Normaali maski tai Varaajapussimaski",
    virtaus: "5–8 l/min tai 10–15 l/min",
    taso: "Tarpeen mukaan (SpO2 < 95 %)",
    ohje: "Anna lisähappea vain jos SpO2 < 95 % (tavoite ≥ 95 %). Lievään hypoksiaan normaali maski 5–8 l/min (aina väh. 5 l/min). Vaikeammassa hypoksiassa varaajapussimaski 10–15 l/min.",
  },
};

/**
 * Hakee tehtävälle tai hälytyskoodille tehtäväluokan mukaisen yksinkertaisen happisuosituksen.
 */
export function getHappisuositus(ohjeOrCode) {
  if (!ohjeOrCode) return HAPPI_KATEGORIAT.moderate;

  let ohje = ohjeOrCode;
  if (typeof ohjeOrCode === "string") {
    ohje = getOhjeByCode(ohjeOrCode);
  }

  if (ohje && ohje.happisuositus) {
    return ohje.happisuositus;
  }

  const code = ohje ? String(ohje.code || "") : "";
  const cat = ohje ? ohje.category : "";

  if (
    code.startsWith("700") ||
    code.startsWith("701") ||
    cat === "resuscitation"
  ) {
    return HAPPI_KATEGORIAT.resuscitation;
  }

  if (
    code === "703" ||
    (cat === "breathing" && !["711", "713", "714"].includes(code))
  ) {
    return HAPPI_KATEGORIAT.breathing;
  }

  if (
    [
      "702",
      "711",
      "713",
      "714",
      "740",
      "741",
      "742",
      "743",
      "744",
      "745",
      "746",
      "747",
      "752",
      "773",
      "030",
      "031",
      "032",
      "033",
      "034",
    ].includes(code) ||
    ["trauma", "poisoning", "allergic", "violence", "consciousness"].includes(
      cat,
    )
  ) {
    return HAPPI_KATEGORIAT.critical;
  }

  return HAPPI_KATEGORIAT.moderate;
}
